import BomateWordmark from '../icons/BomateWordmark';
import ConversationExecutionStatus, { conversationExecutionPhase } from './ConversationExecutionStatus';
import { parseScenarioTriggers } from '../../services/scenarioPresentation';
import SecretaryIdentity from './SecretaryIdentity';
import CoworkHistoryNavigator from './CoworkHistoryNavigator';
import MemoryFilesDialog from './MemoryFilesDialog';
import BackgroundTasksPanel from './BackgroundTasksPanel';
import { backgroundTaskAnchor } from '../../utils/backgroundTaskAnchor';
import AgentSnapshotBadge from './AgentSnapshotBadge';
import ConversationResults from './ConversationResults';
import ConversationTurnFooter, { formatCompletionMeta } from './ConversationTurnFooter';
import { CopyButton } from './ConversationResultActions';
import ConversationActivityDisclosure from './ConversationActivityDisclosure';
import { buildDisplayItems, buildConversationTurns, buildSubagentRenderPlan, buildActivitySections, buildToolSequences, isActivityFailure, type IndexedAssistantItem, type ToolGroupItem, type AssistantTurnItem, type ConversationTurn } from '../../lib/conversationPresentation';
import ConversationTurnDeleteDialog from './ConversationTurnDeleteDialog';
import { buildConversationLink } from '../../services/conversationLink';
import { buildAttachmentAccessUrl } from '../../services/hostInputAttachmentToken';
import { useConversationScroll } from './useConversationScroll';
import ConversationSearch from './ConversationSearch';
import { splitThinkingLeakSegments, stripThinkingLeak } from '../../utils/thinkingLeak';
import { useThinkingPreference } from './useThinkingPreference';
import { useSessionCredit, SessionCreditOverview, type SessionCreditSnapshot } from '../account/SessionCredit';
import { readTurnSummary } from '../../utils/coworkTurnSummary';
import React, { useRef, useEffect, useLayoutEffect, useState, useCallback, useMemo } from 'react';
import type { CoworkPromptInputRef } from './CoworkPromptInput';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { fetchDeliverableArtifactBlob, isArtifactDownloadUrl } from '../../services/deliverableArtifacts';
import { i18nService } from '../../services/i18n';
import type { CoworkMessage, CoworkMessageMetadata, CoworkExecutionMode, SandboxProfile, SandboxImage } from '../../types/cowork';
import type { Skill } from '../../types/skill';
import CoworkPromptInput from './CoworkPromptInput';
import CoworkQueuedMessagesBar from './CoworkQueuedMessagesBar';
import MarkdownContent from '../MarkdownContent';
import ResultManifestCards from './ResultManifestCards';
import { readResultManifest } from '../../../shared/coworkResultManifest';
import { documentArtifactPath } from '../../../shared/documentArtifactPath';
import { createSessionImageLoader, type MarkdownImageLoader } from '../MarkdownImage';
import { parseAllShowWidgets, computePartialWidgetKey, extractTruncatedWidget } from '../../lib/widget-parser';
import { WidgetRenderer } from './WidgetRenderer';
import {
  CheckIcon,
  InformationCircleIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  ShareIcon,
  LinkIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ComputerDesktopIcon,
  CommandLineIcon,
  ArrowTopRightOnSquareIcon,
  ArrowsPointingOutIcon,
  DocumentTextIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  BoltIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import FolderIcon from '../icons/FolderIcon';
import SkillIcon from '../icons/SkillIcon';
import { createConversationImage, ConversationExportError } from '../../utils/conversationImageExport';
import { coworkService } from '../../services/cowork';
import { formatDocumentExtractProgress, isDocumentExtractToolName, resolveDocumentExtractProgress } from './documentExtractProgressText';
import { buildVncViewerUrl, shouldReplaceVncBootstrap } from '../../services/vncAccess';
import { relayService, getTargetClient } from '../../services/relay';
import { dequeuePendingPermission, updateSessionPromptMode } from '../../store/slices/coworkSlice';
import type { CoworkPermissionResult } from '../../types/cowork';
import CoworkInlinePermission from './CoworkInlinePermission';
import FollowupDrawer from '../common/FollowupDrawer';
import CoworkQuickActions from './CoworkQuickActions';
import SidebarToggleIcon from '../icons/SidebarToggleIcon';
import ComposeIcon from '../icons/ComposeIcon';
import WindowTitleBar from '../window/WindowTitleBar';
import { resolveSessionSandboxSelection } from '../../utils/sandboxEnv';
import DeliverablePanel from './DeliverablePanel';
import InlineFilePreview from './InlineFilePreview';
import TerminalPanel from './TerminalPanel';
import TodoWritePanel from './TodoWritePanel';
import CoworkTokenBar from './CoworkTokenBar';
import CoworkUserErrorCard from './CoworkUserErrorCard';
import { type TodoStatus, type ParsedTodoItem, isTodoWriteToolName, parseTodoWriteItems, extractLatestTodoItems } from '../../lib/todo-write-parser';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import type { MyScenario } from '../../services/scenario';
import ScenarioAppIcon from '../scenarios/ScenarioAppIcon';

const IM_PLATFORM_LOGOS: Record<string, string> = {
  dingtalk: '/dingding.png',
  feishu: '/feishu.png',
  wechatwork: '/wecom.svg',
  telegram: '/telegram.svg',
  whatsapp: '/whatsapp.svg',
};

const IMPlatformBadgeIcon: React.FC<{ platform: string }> = ({ platform }) => {
  const key = platform.toLowerCase();
  const logo = IM_PLATFORM_LOGOS[key];
  if (logo) return <img src={logo} alt="" className="h-3.5 w-3.5 object-contain" />;
  if (key === 'whatsapp') {
    return (
      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill="#25D366" />
        <path d="m7.2 17.2 1-2.7a6.4 6.4 0 1 1 2 1.8l-3 .9Z" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M9.4 8.3c.5 2.7 2.2 4.5 5 5.4l1-1.4-1.8-.9-.8.8a5.2 5.2 0 0 1-2.5-2.5l.8-.8-.9-1.7-.8 1.1Z" fill="#fff" />
      </svg>
    );
  }
  if (key === 'wechat') {
    return <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#07c160] text-[8px] font-bold text-white">微</span>;
  }
  return <span className="h-2.5 w-2.5 rounded-full bg-current opacity-70" />;
};

/**
 * BoMate 主会话的档位开关（普通会话的档位在建会话时固化，不给切）。
 *
 * 协作 = 起沙箱、工具与技能全开（做 PPT、跑脚本走这一档，沙箱可打开查看）；
 * 快速 = 不起沙箱的纯对话，只带记忆与检索工具，所以这一档下不展示沙箱徽标与入口。
 * 运行中的会话后端会拒绝切换（409），这里直接禁用按钮并说明原因。
 */
const PromptModeToggle: React.FC<{
  sessionId: string;
  mode: 'collaboration' | 'quick';
  disabled: boolean;
  onSwitched: (mode: 'collaboration' | 'quick') => void;
  onError: (message: string) => void;
}> = ({ sessionId, mode, disabled, onSwitched, onError }) => {
  const [switching, setSwitching] = useState(false);
  const next = mode === 'quick' ? 'collaboration' : 'quick';
  const Icon = mode === 'quick' ? BoltIcon : ChatBubbleLeftRightIcon;

  const handleClick = async () => {
    if (disabled || switching) return;
    setSwitching(true);
    try {
      const result = await window.electron?.cowork?.setSessionPromptMode?.({ sessionId, promptMode: next });
      if (result?.success) {
        onSwitched(next);
      } else {
        onError(result?.error || i18nService.t('coworkPromptModeSwitchFailed'));
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : i18nService.t('coworkPromptModeSwitchFailed'));
    } finally {
      setSwitching(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || switching}
      className={`non-draggable inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide transition-colors ${
        mode === 'quick'
          ? 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400'
          : 'border-indigo-500/30 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400'
      } ${disabled || switching ? 'cursor-not-allowed opacity-60' : 'hover:opacity-80'}`}
      title={disabled
        ? i18nService.t('coworkPromptModeSwitchBusy')
        : i18nService.t(next === 'quick' ? 'coworkPromptModeSwitchToQuick' : 'coworkPromptModeSwitchToCollab')}
    >
      <Icon className="h-3 w-3" />
      <span>{i18nService.t(mode === 'quick' ? 'coworkPromptModeQuick' : 'coworkPromptModeCollab')}</span>
    </button>
  );
};

/** 展示兼容 Relay 桌面会话的运行模式；Web 创建配置仍只允许 sandbox。 */
type DisplayExecutionMode = CoworkExecutionMode | 'local' | 'auto';
const ExecutionBadge: React.FC<{ mode: DisplayExecutionMode; requestedMode?: DisplayExecutionMode }> = ({ mode, requestedMode }) => {
  const isAutoSelection = requestedMode === 'auto' && mode !== 'auto';
  const label = mode === 'sandbox'
    ? i18nService.t('coworkSandboxBadge')
    : mode === 'local'
      ? i18nService.t('coworkLocalBadge')
      : i18nService.t('coworkExecutionModeAuto');
  const fullLabel = isAutoSelection ? `${i18nService.t('coworkExecutionModeAuto')} · ${label}` : label;
  const appearance = mode === 'sandbox'
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500 dark:text-emerald-400'
    : mode === 'local'
      ? 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400'
      : 'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400';

  return (
    <span
      className={`non-draggable inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-wide ${appearance}`}
      title={fullLabel}
    >
      {mode === 'sandbox' ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /></svg>
      ) : mode === 'local' ? (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m12 3 1.9 5.7L19 10l-5.1 1.3L12 17l-1.9-5.7L5 10l5.1-1.3L12 3Z" /></svg>
      )}
      <span className="hidden xl:inline">{fullLabel}</span>
    </span>
  );
};

interface CoworkSessionDetailProps {
  onManageSkills?: () => void;
  onContinue: (prompt: string, skillPrompt?: string) => void | boolean | Promise<void | boolean>;
  onStop: () => void;
  onNavigateHome?: () => void;
  isSidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  onNewChat?: () => void;
  isMobile?: boolean;
  overrideSandboxProfileId?: string | null;
  onSandboxProfileOverride?: (id: string | null) => void;
  overrideSandboxImageId?: string | null;
  onSandboxImageOverride?: (id: string | null) => void;
  activeScenario?: MyScenario | null;
  /** 由外层统一准备场景，仅空闲的云端协作会话提供。 */
  onSelectScenario?: (scenario: MyScenario) => void;
  onDismissScenario?: () => void;
}

const INVALID_FILE_NAME_PATTERN = /[<>:"/\\|?*\u0000-\u001F]/g;

const sanitizeExportFileName = (value: string): string => {
  const sanitized = value.replace(INVALID_FILE_NAME_PATTERN, ' ').replace(/\s+/g, ' ').trim();
  return sanitized || 'cowork-session';
};

const formatExportTimestamp = (value: Date): string => {
  const pad = (num: number): string => String(num).padStart(2, '0');
  return `${value.getFullYear()}${pad(value.getMonth() + 1)}${pad(value.getDate())}-${pad(value.getHours())}${pad(value.getMinutes())}${pad(value.getSeconds())}`;
};

// PushPinIcon component for pin/unpin functionality
const PushPinIcon: React.FC<React.SVGProps<SVGSVGElement> & { slashed?: boolean }> = ({
  slashed,
  ...props
}) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <g transform="rotate(45 12 12)">
      <path d="M9 3h6l-1 5 2 2v2H8v-2l2-2-1-5z" />
      <path d="M12 12v9" />
    </g>
    {slashed && <path d="M5 5L19 19" />}
  </svg>
);

const formatUnknown = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const getStringArray = (value: unknown): string | null => {
  if (!Array.isArray(value)) return null;
  const lines = value.filter((item) => typeof item === 'string') as string[];
  return lines.length > 0 ? lines.join('\n') : null;
};

const getTodoWriteSummary = (items: ParsedTodoItem[]): string => {
  const completedCount = items.filter((item) => item.status === 'completed').length;
  const inProgressCount = items.filter((item) => item.status === 'in_progress').length;
  const pendingCount = items.length - completedCount - inProgressCount;

  const summary = [
    `${items.length} ${i18nService.t('coworkTodoItems')}`,
    `${completedCount} ${i18nService.t('coworkTodoCompleted')}`,
    `${inProgressCount} ${i18nService.t('coworkTodoInProgress')}`,
    `${pendingCount} ${i18nService.t('coworkTodoPending')}`,
  ];

  const activeItem = items.find((item) => item.status === 'in_progress');
  if (activeItem) {
    summary.push(activeItem.primaryText);
  }

  return summary.join(' · ');
};

const getToolInputSummary = (
  toolName: string | undefined,
  toolInput?: Record<string, unknown>
): string | null => {
  if (!toolName || !toolInput) return null;
  const input = toolInput as Record<string, unknown>;
  if (isTodoWriteToolName(toolName)) {
    const items = parseTodoWriteItems(input);
    return items ? getTodoWriteSummary(items) : null;
  }

  switch (toolName) {
    case 'Bash':
      return typeof input.command === 'string'
        ? input.command
        : getStringArray(input.commands);
    case 'Read':
    case 'Write':
    case 'Edit':
    case 'MultiEdit':
      return typeof input.file_path === 'string' ? input.file_path : null;
    case 'Glob':
    case 'Grep':
      return typeof input.pattern === 'string' ? input.pattern : null;
    case 'Task':
      return typeof input.description === 'string' ? input.description : null;
    case 'WebFetch':
      return typeof input.url === 'string' ? input.url : null;
    default:
      return null;
  }
};

const formatToolInput = (
  toolName: string | undefined,
  toolInput?: Record<string, unknown>
): string | null => {
  if (!toolInput) return null;
  const summary = getToolInputSummary(toolName, toolInput);
  if (summary && summary.trim()) {
    return summary;
  }
  return formatUnknown(toolInput);
};

const getToolInputString = (
  toolInput: Record<string, unknown> | undefined,
  keys: string[],
): string | null => {
  if (!toolInput) return null;
  for (const key of keys) {
    const value = toolInput[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

const formatToolUrl = (value: string): string => {
  try {
    const url = new URL(value);
    return url.hostname || value;
  } catch {
    return value.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  }
};

const formatReadableToolName = (toolName: string): string => {
  const unscopedName = toolName.split('__').filter(Boolean).pop() ?? toolName;
  return unscopedName
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();
};

const getToolActionLabel = (
  toolName: string,
  toolInput?: Record<string, unknown>,
): string => {
  const normalizedToolName = toolName.split('__').filter(Boolean).pop() ?? toolName;
  const withSubject = (actionKey: string, subject?: string | null): string => {
    const action = i18nService.t(actionKey);
    return subject ? `${action} ${subject}` : action;
  };

  switch (normalizedToolName) {
    case 'WebSearch':
    case 'web_search':
      return withSubject('coworkToolActionSearch', getToolInputString(toolInput, ['query', 'q', 'search_query']));
    case 'WebFetch':
    case 'web_fetch': {
      const url = getToolInputString(toolInput, ['url', 'uri']);
      return withSubject('coworkToolActionView', url ? formatToolUrl(url) : null);
    }
    case 'Read':
      return withSubject('coworkToolActionRead', getToolInputString(toolInput, ['file_path', 'path']));
    case 'Write':
      return withSubject('coworkToolActionWrite', getToolInputString(toolInput, ['file_path', 'path']));
    case 'Edit':
    case 'MultiEdit':
      return withSubject('coworkToolActionEdit', getToolInputString(toolInput, ['file_path', 'path']));
    case 'Glob':
    case 'Grep':
      return withSubject('coworkToolActionFind', getToolInputString(toolInput, ['pattern', 'query']));
    case 'Bash':
      return withSubject('coworkToolActionRun', getToolInputString(toolInput, ['command']));
    case 'Skill':
      return withSubject('coworkToolActionSkill', getToolInputString(toolInput, ['skill']));
    case 'Task':
    case 'Agent':
      return withSubject('coworkToolActionDelegate', getToolInputString(toolInput, ['description', 'prompt']));
    default: {
      if (isTodoWriteToolName(normalizedToolName)) return i18nService.t('coworkToolActionPlan');
      const readableName = formatReadableToolName(normalizedToolName);
      const subject = getToolInputString(toolInput, [
        'query', 'q', 'url', 'uri', 'file_path', 'path', 'description', 'command', 'pattern', 'name',
      ]);
      return subject ? `${readableName} ${subject}` : readableName;
    }
  }
};

const hasText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const getToolResultDisplay = (message: CoworkMessage): string => {
  if (hasText(message.content)) {
    return message.content;
  }
  if (hasText(message.metadata?.toolResult)) {
    return message.metadata?.toolResult ?? '';
  }
  if (hasText(message.metadata?.error)) {
    return message.metadata?.error ?? '';
  }
  return '';
};

const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const stripHashAndQuery = (value: string): string => value.split('#')[0].split('?')[0];

const stripFileProtocol = (value: string): string => {
  let cleaned = value.replace(/^file:\/\//i, '');
  if (/^\/[A-Za-z]:/.test(cleaned)) {
    cleaned = cleaned.slice(1);
  }
  return cleaned;
};

const hasScheme = (value: string): boolean => /^[a-z][a-z0-9+.-]*:/i.test(value);

const isAbsolutePath = (value: string): boolean => (
  value.startsWith('/') || /^[A-Za-z]:[\\/]/.test(value)
);

const isRelativePath = (value: string): boolean => !isAbsolutePath(value) && !hasScheme(value);
const SANDBOX_WORKSPACE_GUEST_ROOT = '/workspace/project';
const SANDBOX_WORKSPACE_LEGACY_ROOT = '/workspace';
const SANDBOX_WORKSPACE_RESERVED_DIRS = new Set(['skills', 'ipc', 'tmp']);

const isReservedSandboxSegment = (relativePath: string): boolean => {
  const [firstSegment] = relativePath.split('/');
  return Boolean(firstSegment && SANDBOX_WORKSPACE_RESERVED_DIRS.has(firstSegment.toLowerCase()));
};

const mapSandboxGuestPathToCwd = (filePath: string, cwd?: string): string | null => {
  if (!cwd) return null;

  const normalizedPath = filePath.replace(/\\/g, '/');
  const normalizedCwd = cwd.replace(/[\\/]+$/, '');

  if (
    normalizedPath === SANDBOX_WORKSPACE_GUEST_ROOT
    || normalizedPath.startsWith(`${SANDBOX_WORKSPACE_GUEST_ROOT}/`)
  ) {
    const relativePath = normalizedPath
      .slice(SANDBOX_WORKSPACE_GUEST_ROOT.length)
      .replace(/^\/+/, '');
    if (relativePath && isReservedSandboxSegment(relativePath)) {
      return null;
    }
    return relativePath ? `${normalizedCwd}/${relativePath}` : normalizedCwd;
  }

  if (
    normalizedPath !== SANDBOX_WORKSPACE_LEGACY_ROOT
    && !normalizedPath.startsWith(`${SANDBOX_WORKSPACE_LEGACY_ROOT}/`)
  ) {
    return null;
  }

  const legacyRelativePath = normalizedPath
    .slice(SANDBOX_WORKSPACE_LEGACY_ROOT.length)
    .replace(/^\/+/, '');
  if (!legacyRelativePath) {
    return normalizedCwd;
  }

  if (isReservedSandboxSegment(legacyRelativePath)) {
    return null;
  }

  return `${normalizedCwd}/${legacyRelativePath}`;
};

const mapSandboxGuestPathsInText = (value: string, _cwd?: string): string => {
  // In sandbox mode, keep the original container paths (e.g. /workspace/project/...)
  // instead of mapping them to host cwd paths.
  return value;
};

const parseRootRelativePath = (value: string): string | null => {
  const trimmed = value.trim();
  if (!/^file:\/\//i.test(trimmed)) return null;
  const separatorIndex = trimmed.indexOf('::');
  if (separatorIndex < 0) return null;

  const rootPart = trimmed.slice(0, separatorIndex);
  const relativePart = trimmed.slice(separatorIndex + 2);
  if (!relativePart.trim()) return null;

  const rootPath = safeDecodeURIComponent(stripFileProtocol(stripHashAndQuery(rootPart)));
  const relativePath = safeDecodeURIComponent(stripHashAndQuery(relativePart));
  if (!rootPath || !relativePath) return null;

  const normalizedRoot = rootPath.replace(/[\\/]+$/, '');
  const normalizedRelative = relativePath.replace(/^[\\/]+/, '');
  if (!normalizedRelative) return null;

  return `${normalizedRoot}/${normalizedRelative}`;
};

const normalizeLocalPath = (
  value: string
): { path: string; isRelative: boolean; isAbsolute: boolean } | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const fileScheme = /^file:\/\//i.test(trimmed);
  const schemePresent = hasScheme(trimmed);
  if (schemePresent && !fileScheme && !isAbsolutePath(trimmed)) return null;

  let raw = trimmed;
  if (fileScheme) {
    raw = stripFileProtocol(raw);
  }
  raw = stripHashAndQuery(raw);
  const decoded = safeDecodeURIComponent(raw);
  const path = decoded || raw;
  if (!path) return null;

  const isAbsolute = isAbsolutePath(path);
  const isRelative = isRelativePath(path);
  return { path, isRelative, isAbsolute };
};

const toAbsolutePathFromCwd = (filePath: string, cwd: string): string => {
  if (isAbsolutePath(filePath)) {
    return filePath;
  }
  return `${cwd.replace(/\/$/, '')}/${filePath.replace(/^\.\//, '')}`;
};

const SDK_PLACEHOLDER_RE = /^\(no content\)$/i;
// 只有省略号的助手消息：模型偶发吐出一条内容仅为 "..." / "…" 的最终消息（实测一个会话里
// 出现 6 条，metadata 只有 {"isStreaming":false}），渲染出来就是一串孤零零的空气泡。
// 仅在**非流式**时过滤——流式途中内容可能正好是省略号，之后还会继续长出来。
const ELLIPSIS_ONLY_RE = /^[.。·]{2,}$|^…+$/;

const isRenderableAssistantOrSystemMessage = (message: CoworkMessage): boolean => {
  if (readResultManifest(message.metadata?.resultManifest)?.artifacts.length) return true;
  if (message.metadata?.userError) return true;
  if (hasText(message.content)) {
    // Filter out SDK placeholder text for empty text blocks
    if (SDK_PLACEHOLDER_RE.test(message.content.trim())) return false;
    if (!message.metadata?.isStreaming && ELLIPSIS_ONLY_RE.test(message.content.trim())) return false;
    return true;
  }
  if (hasText(message.metadata?.error)) {
    return true;
  }
  if (message.metadata?.isThinking) {
    return Boolean(message.metadata?.isStreaming);
  }
  return false;
};

const isVisibleAssistantTurnItem = (item: AssistantTurnItem): boolean => {
  if (item.type === 'assistant' || item.type === 'system') {
    return isRenderableAssistantOrSystemMessage(item.message);
  }
  if (item.type === 'tool_result') {
    return hasText(getToolResultDisplay(item.message));
  }
  return true;
};

const getVisibleAssistantItems = (assistantItems: AssistantTurnItem[]): AssistantTurnItem[] =>
  assistantItems.filter(isVisibleAssistantTurnItem);

const hasRenderableAssistantContent = (turn: ConversationTurn): boolean => (
  getVisibleAssistantItems(turn.assistantItems).length > 0
);

/**
 * 子 Agent 区域卡片:每个子 Agent 一块独立可折叠的区域。
 * 运行中默认展开(用户在看进度),结束后默认收起;手动切换后以用户选择为准。
 */
const SubagentSectionCard: React.FC<{
  label: string;
  completed: boolean;
  stepCount: number;
  collapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ label, completed, stepCount, collapsed, onToggle, children }) => (
  <div className="rounded-xl border border-violet-200/80 dark:border-violet-800/40 bg-violet-50/40 dark:bg-violet-900/10 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-3 py-2 text-left select-none hover:bg-violet-100/50 dark:hover:bg-violet-900/20 transition-colors"
    >
      <svg
        className={`h-3.5 w-3.5 shrink-0 text-violet-400 transition-transform ${collapsed ? '' : 'rotate-90'}`}
        viewBox="0 0 16 16"
        fill="currentColor"
        aria-hidden
      >
        <path d="M6 3.5 11 8l-5 4.5v-9z" />
      </svg>
      <span className="px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300 text-[11px] font-medium leading-none shrink-0">
        子Agent
      </span>
      <span className="text-[13px] text-gray-700 dark:text-gray-200 truncate">{label}</span>
      <span className="ml-auto flex items-center gap-2 shrink-0 text-[11px] text-gray-400 dark:text-gray-500">
        <span>{stepCount} 步</span>
        {completed ? (
          <span className="text-green-500 dark:text-green-400">✓ 已完成</span>
        ) : (
          <span className="flex items-center gap-1 text-blue-500 dark:text-blue-400">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
            运行中
          </span>
        )}
      </span>
    </button>
    {!collapsed && (
      <div className="px-3 pb-2.5 pt-0.5 space-y-3 border-t border-violet-200/50 dark:border-violet-800/30">
        {children}
      </div>
    )}
  </div>
);

const getToolResultLineCount = (result: string): number => {
  if (!result) return 0;
  return result.split('\n').length;
};

const TodoWriteSummaryBadges: React.FC<{ items: ParsedTodoItem[] }> = ({ items }) => {
  const completed = items.filter((i) => i.status === 'completed').length;
  const inProgress = items.filter((i) => i.status === 'in_progress').length;
  const pending = items.length - completed - inProgress;
  const activeItem = items.find((i) => i.status === 'in_progress');

  return (
    <span className="inline-flex items-center gap-1 flex-wrap text-[10px] font-medium">
      <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
        {items.length} {i18nService.t('coworkTodoItems')}
      </span>
      {completed > 0 && (
        <span className="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400">
          {completed} {i18nService.t('coworkTodoCompleted')}
        </span>
      )}
      {inProgress > 0 && (
        <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400">
          {inProgress} {i18nService.t('coworkTodoInProgress')}
        </span>
      )}
      {pending > 0 && (
        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700/60 text-gray-500 dark:text-gray-400">
          {pending} {i18nService.t('coworkTodoPending')}
        </span>
      )}
      {activeItem && (
        <span className="text-[10px] dark:text-claude-darkTextSecondary/70 text-claude-textSecondary/70 truncate max-w-[200px]">
          {activeItem.primaryText}
        </span>
      )}
    </span>
  );
};

const TodoWriteInputView: React.FC<{ items: ParsedTodoItem[] }> = ({ items }) => {
  const getStatusCheckboxClass = (status: TodoStatus): string => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/10 border-green-500 text-green-500';
      case 'in_progress':
        return 'bg-transparent border-blue-500';
      case 'pending':
      case 'unknown':
      default:
        return 'bg-transparent dark:border-claude-darkTextSecondary/60 border-claude-textSecondary/60';
    }
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div
          key={`todo-item-${index}`}
          className="flex items-start gap-2"
        >
          <span className={`mt-0.5 h-4 w-4 rounded-[4px] border flex-shrink-0 inline-flex items-center justify-center ${getStatusCheckboxClass(item.status)}`}>
            {item.status === 'completed' && <CheckIcon className="h-3 w-3 stroke-[2.5]" />}
          </span>
          <div className="min-w-0 flex-1">
            <div className={`text-xs whitespace-pre-wrap break-words leading-5 ${
              item.status === 'completed'
                ? 'dark:text-claude-darkTextSecondary/70 text-claude-textSecondary/80'
                : 'dark:text-claude-darkText text-claude-text'
            }`}>
              {item.primaryText}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ToolCallGroup: React.FC<{
  group: ToolGroupItem;
  searchFocus?: number;
  mapDisplayText?: (value: string) => string;
}> = ({
  group,
  searchFocus = 0,
  mapDisplayText,
}) => {
  const { toolUse, toolResult } = group;
  const toolName = typeof toolUse.metadata?.toolName === 'string' ? toolUse.metadata.toolName : 'Tool';
  const toolInput = toolUse.metadata?.toolInput;
  const isTodoWriteTool = isTodoWriteToolName(toolName);
  const todoItems = isTodoWriteTool ? parseTodoWriteItems(toolInput) : null;
  const mapText = mapDisplayText ?? ((value: string) => value);
  const toolInputDisplayRaw = formatToolInput(toolName, toolInput);
  const toolInputDisplay = toolInputDisplayRaw ? mapText(toolInputDisplayRaw) : null;
  const toolActionLabel = mapText(getToolActionLabel(toolName, toolInput));
  const toolResultDisplayRaw = toolResult ? getToolResultDisplay(toolResult) : '';
  const toolResultDisplay = mapText(toolResultDisplayRaw);
  const isToolError = Boolean(toolResult?.metadata?.isError || toolResult?.metadata?.error);
  const [isExpanded, setIsExpanded] = useState(false);
  useEffect(() => { if (searchFocus) setIsExpanded(true); }, [searchFocus]);
  // DocumentExtract（含历史别名）按页解析进度：仅在工具尚无结果且会话运行中时展示，可停止本轮。
  const toolUseIdForProgress = typeof toolUse.metadata?.toolUseId === 'string' ? toolUse.metadata.toolUseId : '';
  const isDocumentExtractTool = isDocumentExtractToolName(toolName);
  const progressSessionId = useSelector((state: RootState) => state.cowork.currentSession?.id ?? '');
  const progressSessionRunning = useSelector((state: RootState) => state.cowork.currentSession?.status === 'running');
  const documentExtractOwnProgress = useSelector((state: RootState) => (
    isDocumentExtractTool && toolUseIdForProgress && progressSessionId
      ? state.cowork.documentExtractProgressBySession[progressSessionId]?.[toolUseIdForProgress]
      : undefined
  ));
  const documentExtractLatestProgress = useSelector((state: RootState) => (
    isDocumentExtractTool && progressSessionId ? state.cowork.documentExtractLatestBySession[progressSessionId] : undefined
  ));
  const documentExtractProgressView = isDocumentExtractTool && !toolResult && progressSessionRunning
    ? formatDocumentExtractProgress(
      resolveDocumentExtractProgress(documentExtractOwnProgress, documentExtractLatestProgress, toolInput),
      i18nService.getLanguage(),
    )
    : null;

  // Check if this is a Bash-like tool that should show terminal style
  const isBashTool = toolName === 'Bash';

  // SendUserMessage 是模型给用户的正式答复通道（SDK Brief 模式），不是普通工具调用。
  // Web 端此前完全没适配它：整个 renderer 对 SendUserMessage 零处理，于是模型用它
  // 投递的最终回答被折叠成一行灰色工具卡，用户在页面上看不到任何答复。实测一个
  // 联网检索会话跑了 288 秒、写出简报并发给用户，Web 端 assistant 消息数为 0，
  // 页面底部只有一行 "SendUserMessage {...}"，简报正文完全不可见。
  // 这里把它当作助手正文渲染。
  const sendUserMessageText = (() => {
    const input = toolInput as { message?: unknown; content?: unknown; to?: unknown; recipient?: unknown } | undefined;
    const pick = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null);
    if (toolName === 'SendUserMessage') return pick(input?.message);
    // SendMessage 本是 agent 间通信工具，但实测部分模型（GLM-5.2）在工具表里够不到
    // SendUserMessage——两次 ToolSearch 返回的候选都只有 SendMessage/AskUserQuestion，
    // 于是它退而用 SendMessage 并把 to 设成 "user" 来投递给用户。这种情况下 message
    // 就是给用户的正式答复，不渲染就等于用户看不到答案。只认收件人明确是 user 的，
    // 避免把 agent 之间的往来暴露给用户。
    if (toolName === 'SendMessage' && (input?.to === 'user' || input?.recipient === 'user')) {
      const text = pick(input?.content) ?? pick(input?.message);
      // 模型偶发把 status 枚举值误填进 message/content（实测出现过 "normal"）。
      // 这种明显不是答复的内容退回工具卡，免得正文里冒出一个孤零零的状态词。
      if (text && text !== 'normal' && text !== 'proactive') return text;
      return null;
    }
    return null;
  })();

  // Inline HTML preview under a successful Write tool call producing an
  // .html/.htm file. Matches boclaw-app `_showInlineWritePreview` at
  // lib/widgets/message_list.dart:663-673 one-for-one.
  const inlinePreviewFilePath =
    toolName === 'Write' &&
    typeof (toolInput as { file_path?: unknown } | undefined)?.file_path === 'string'
      ? ((toolInput as { file_path?: unknown }).file_path as string)
      : null;
  const inlinePreviewContent =
    toolName === 'Write' &&
    typeof (toolInput as { content?: unknown } | undefined)?.content === 'string'
      ? ((toolInput as { content?: unknown }).content as string)
      : null;
  const showInlinePreview = Boolean(
    toolResult
    && !isToolError
    && inlinePreviewFilePath
    && inlinePreviewContent
    && /\.(html|htm)$/i.test(inlinePreviewFilePath),
  );

  // 投递失败时仍按工具卡渲染，让用户看得到错误（例如缺少必填 status 的参数校验失败）。
  if (sendUserMessageText && !isToolError) {
    return (
      <div className="py-1">
        <MarkdownContent
          content={mapText(sendUserMessageText)}
          className="prose dark:prose-invert max-w-none"
        />
      </div>
    );
  }

  const statusLabel = !toolResult
    ? i18nService.t('coworkToolRunning')
    : null;

  return (
    <div className="relative">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="group -mx-2 flex w-[calc(100%+1rem)] items-start gap-2.5 rounded-xl px-2 py-2 text-left transition-colors hover:bg-black/[0.025] dark:hover:bg-white/[0.035]"
          aria-expanded={isExpanded}
          title={toolActionLabel}
        >
          <span className={`mt-0.5 flex h-[15px] w-[15px] flex-shrink-0 items-center justify-center rounded-full text-white ${
            !toolResult
              ? 'bg-blue-500'
              : isToolError
                ? 'bg-gray-400 dark:bg-gray-500'
                : 'bg-emerald-500'
          }`}>
            {!toolResult ? (
              <EllipsisHorizontalIcon className="h-2.5 w-2.5 animate-pulse stroke-[2.5]" />
            ) : isToolError ? (
              <span className="text-[10px] font-semibold leading-none">!</span>
            ) : (
              <CheckIcon className="h-2.5 w-2.5 stroke-[2.5]" />
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex min-w-0 items-center gap-2">
              <span className="min-w-0 truncate text-xs font-normal leading-5 dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {toolActionLabel}
              </span>
              {isTodoWriteTool && todoItems && <TodoWriteSummaryBadges items={todoItems} />}
            </span>
            {statusLabel && (
              <span className="mt-0.5 block text-[11px] dark:text-claude-darkTextSecondary/50 text-claude-textSecondary/50">
                {statusLabel}
              </span>
            )}
          </span>
          <ChevronRightIcon className={`mt-0.5 h-[13px] w-[13px] flex-shrink-0 dark:text-claude-darkTextSecondary/45 text-claude-textSecondary/45 transition-transform duration-200 ${
            isExpanded ? 'rotate-90' : ''
          }`} />
        </button>

        {documentExtractProgressView && (
          <div className="ml-8 mt-1 flex items-center gap-2" data-testid="document-extract-progress">
            <div className="min-w-0 flex-1">
              <div className="truncate text-[10px] dark:text-claude-darkTextSecondary/70 text-claude-textSecondary/70">
                {documentExtractProgressView.label}
              </div>
              <div className="mt-0.5 h-1 w-full max-w-[240px] overflow-hidden rounded-full dark:bg-claude-darkBorder bg-claude-border">
                <div
                  className={`h-full rounded-full bg-claude-accent transition-all duration-300 ${documentExtractProgressView.percent === null ? 'w-1/3 animate-pulse' : ''}`}
                  style={documentExtractProgressView.percent === null ? undefined : { width: `${documentExtractProgressView.percent}%` }}
                />
              </div>
            </div>
            <button
              type="button"
              className="rounded border px-1.5 py-0.5 text-[10px] dark:border-claude-darkBorder border-claude-border dark:text-claude-darkTextSecondary text-claude-textSecondary hover:border-red-400 hover:text-red-500"
              onClick={() => { if (progressSessionId) void coworkService.stopSession(progressSessionId); }}
            >
              {i18nService.t('coworkDocumentExtractStop')}
            </button>
          </div>
        )}

        {showInlinePreview && inlinePreviewFilePath && inlinePreviewContent !== null && (
          <div className="ml-8">
            <InlineFilePreview filePath={inlinePreviewFilePath} content={inlinePreviewContent} />
          </div>
        )}

        {isExpanded && (
          <div className="mb-2 ml-8 mt-1 rounded-xl border dark:border-claude-darkBorder/70 border-claude-border/70 dark:bg-claude-darkSurface/35 bg-claude-surface/35 p-3">
            {isBashTool ? (
              // Terminal-style display for Bash commands
              <div className="overflow-hidden rounded-lg border dark:border-claude-darkBorder border-claude-border">
                <div className="flex items-center gap-1.5 px-3 py-1.5 dark:bg-claude-darkSurface bg-claude-surfaceInset">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
                  <div className="h-2.5 w-2.5 rounded-full bg-yellow-500" />
                  <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
                  <span className="ml-2 text-[10px] font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">Terminal</span>
                </div>
                <div className="max-h-72 overflow-y-auto px-3 py-3 font-mono text-xs dark:bg-claude-darkSurfaceInset bg-claude-surfaceInset">
                  {toolInputDisplay && (
                    <div className="dark:text-claude-darkText text-claude-text">
                      <span className="text-claude-accent select-none">$ </span>
                      <span className="whitespace-pre-wrap break-words">{toolInputDisplay}</span>
                    </div>
                  )}
                  {toolResult && toolResultDisplay && (
                    <div className={`mt-1.5 whitespace-pre-wrap break-words ${
                      isToolError ? 'text-red-400' : 'dark:text-claude-darkTextSecondary text-claude-textSecondary'
                    }`}>
                      {toolResultDisplay}
                    </div>
                  )}
                  {!toolResult && (
                    <div className="mt-1.5 italic dark:text-claude-darkTextSecondary/60 text-claude-textSecondary/60">
                      {i18nService.t('coworkToolRunning')}
                    </div>
                  )}
                </div>
              </div>
            ) : isTodoWriteTool && todoItems ? (
              <TodoWriteInputView items={todoItems} />
            ) : (
              <div className="space-y-3">
                {toolInputDisplay && (
                  <div>
                    <div className="mb-1 text-[10px] font-medium uppercase tracking-wider dark:text-claude-darkTextSecondary/70 text-claude-textSecondary/70">
                      {i18nService.t('coworkToolInput')}
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      <pre className="whitespace-pre-wrap break-words font-mono text-xs dark:text-claude-darkText text-claude-text">
                        {toolInputDisplay}
                      </pre>
                    </div>
                  </div>
                )}
                {toolResult && (
                  <div>
                    <div className="mb-1 text-[10px] font-medium uppercase tracking-wider dark:text-claude-darkTextSecondary/70 text-claude-textSecondary/70">
                      {i18nService.t('coworkToolResult')}
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      <pre className={`whitespace-pre-wrap break-words font-mono text-xs ${
                        isToolError ? 'text-red-500' : 'dark:text-claude-darkText text-claude-text'
                      }`}>
                        {toolResultDisplay}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
    </div>
  );
};

const IMAGE_EXTS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'];
// Match "输入文件: /path/to/img.png" or "input file: C:\path\img.png" (with optional bullet prefix)
const ATTACH_RE = /^\s*(?:[-*]\s*)?(?:输入文件|input\s*file)\s*[:：]\s*(.+?)\s*$/i;

// ── Auth helper for file read API ──────────────────────────────────
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('boclaw_token');
  const userId = localStorage.getItem('boclaw_user_id');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (userId) headers['X-Boclaw-User-Id'] = userId;
  return headers;
}

// ── File preview data type ─────────────────────────────────────────
type FilePreviewData =
  | { type: 'image'; dataUrl: string; fileName: string }
  | { type: 'text'; content: string; fileName: string }
  | { type: 'binary'; fileName: string; size: number; downloadUrl?: string }
  | { type: 'error'; message: string };

// ── File preview modal ─────────────────────────────────────────────
const FilePreviewModal: React.FC<{
  filePath: string;
  data: FilePreviewData | null;
  loading: boolean;
  onClose: () => void;
}> = ({ filePath, data, loading, onClose }) => {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');

  /** 下载 API 必须携带登录凭据；桌面 Relay 返回的 data URL 继续使用浏览器直接下载。 */
  const downloadAttachment = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (data?.type !== 'binary' || !data.downloadUrl?.startsWith('/api/')) return;
    event.preventDefault();
    if (downloading) return;
    setDownloading(true);
    setDownloadError('');
    try {
      const response = await fetch(data.downloadUrl, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error(`下载失败 (${response.status})`);
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = data.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : '下载失败');
    } finally {
      setDownloading(false);
    }
  };
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const fileName = filePath.split(/[\\/]/).pop() || filePath;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="relative max-w-3xl w-full max-h-[80vh] mx-4 rounded-xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">{fileName}</span>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-400 text-sm">加载中...</div>
          )}
          {!loading && data?.type === 'image' && (
            <img src={data.dataUrl} alt={data.fileName} className="max-w-full mx-auto rounded" />
          )}
          {!loading && data?.type === 'text' && (
            <pre className="text-xs leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words font-mono bg-gray-50 dark:bg-gray-900 rounded-lg p-4 max-h-[60vh] overflow-auto">
              {data.content}
            </pre>
          )}
          {!loading && data?.type === 'binary' && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-sm gap-2">
              <DocumentTextIcon className="h-12 w-12" />
              <span>{data.fileName}</span>
              <span className="text-xs">{formatFileSize(data.size)}</span>
              <span className="text-xs">二进制文件，无法预览</span>
              {data.downloadUrl && (
                <a
                  href={data.downloadUrl}
                  download={data.fileName}
                  onClick={downloadAttachment}
                  aria-disabled={downloading}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-claude-accent px-3 py-2 text-xs font-medium text-white hover:opacity-90"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {downloading ? '下载中...' : '下载文件'}
                </a>
              )}
              {downloadError && <span role="alert" className="text-red-500">{downloadError}</span>}
            </div>
          )}
          {!loading && data?.type === 'error' && (
            <div className="flex items-center justify-center py-12 text-red-400 text-sm">{data.message}</div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Format file size helper ────────────────────────────────────────
function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

// ── Attachment file card ───────────────────────────────────────────
const AttachmentFileCard: React.FC<{
  filePath: string;
  isImage: boolean;
  imageDataURL?: string;
  readUrl: string;
  onPreview: (filePath: string) => void;
}> = ({ filePath, isImage, imageDataURL, readUrl, onPreview }) => {
  const fileName = filePath.split(/[\\/]/).pop() || filePath;
  const [fileSize, setFileSize] = useState<number | null>(null);

  // Fetch file metadata to get size (for non-image files)
  useEffect(() => {
    if (isImage) return;
    let cancelled = false;
    fetch(readUrl, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!cancelled && data?.size != null) setFileSize(data.size);
        // For text type, estimate from content length
        if (!cancelled && data?.type === 'text' && data.content) setFileSize(new Blob([data.content]).size);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [readUrl, isImage]);

  if (isImage && imageDataURL) {
    return (
      <div
        className="cursor-pointer rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-claude-accent/50 transition-colors"
        onClick={() => onPreview(filePath)}
        title={filePath}
      >
        <img
          src={imageDataURL}
          alt={fileName}
          className="max-h-48 max-w-full object-contain"
        />
      </div>
    );
  }

  if (isImage) {
    return (
      <div
        className="flex items-center justify-center h-24 w-32 rounded-lg bg-black/5 dark:bg-white/5 text-xs text-[var(--text-tertiary)] cursor-pointer hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
        onClick={() => onPreview(filePath)}
        title={filePath}
      >
        {fileName}
      </div>
    );
  }

  // 非图片:桌面端样式 —— 彩色扩展名徽标块 + 文件名 + 「EXT 文件 · 大小」
  const ext = (fileName.split('.').pop() || '').toLowerCase();
  const extLabel = ext ? ext.toUpperCase() : 'FILE';
  const colorMap: Record<string, { bg: string; text: string }> = {
    pdf:  { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400' },
    doc:  { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400' },
    docx: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400' },
    xls:  { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400' },
    xlsx: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400' },
    ppt:  { bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400' },
    pptx: { bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400' },
    txt:  { bg: 'bg-gray-500/10', text: 'text-gray-600 dark:text-gray-400' },
    csv:  { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400' },
  };
  const colors = colorMap[ext] || { bg: 'bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400' };
  return (
    <div
      className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-black/[0.03] dark:bg-white/[0.06] cursor-pointer hover:bg-black/[0.06] dark:hover:bg-white/[0.1] transition-colors max-w-[20rem]"
      onClick={() => onPreview(filePath)}
      title={filePath}
    >
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${colors.bg}`}>
        <span className={`text-[10px] font-bold ${colors.text}`}>{extLabel.slice(0, 5)}</span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium truncate dark:text-claude-darkText text-claude-text leading-tight">{fileName}</div>
        <div className="text-[10px] text-claude-textSecondary dark:text-claude-darkTextSecondary mt-0.5">
          {extLabel} {i18nService.t('coworkFile')}{fileSize != null ? ` · ${formatFileSize(fileSize)}` : ''}
        </div>
      </div>
    </div>
  );
};

const UserMessageItem: React.FC<{
  message: CoworkMessage;
  skills: Skill[];
  executionMode?: CoworkExecutionMode;
  sessionId?: string;
}> = ({ message, skills, sessionId }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageDataURLs, setImageDataURLs] = useState<Record<string, string>>({});
  const [previewFile, setPreviewFile] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<FilePreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const desktopTargetClientId = sessionId ? getTargetClient(sessionId) : null;

  // Get skills used for this message
  const messageSkillIds = (message.metadata as CoworkMessageMetadata)?.skillIds || [];
  const messageSkills = messageSkillIds
    .map(id => skills.find(s => s.id === id))
    .filter((s): s is NonNullable<typeof s> => s !== undefined);

  const { textContent, imagePaths, filePaths } = useMemo(() => {
    const lines = message.content.split('\n');
    const images: string[] = [];
    const files: string[] = [];
    const textLines: string[] = [];
    for (const line of lines) {
      const match = line.match(ATTACH_RE);
      if (match) {
        if (IMAGE_EXTS.some(e => match[1].toLowerCase().endsWith(e))) {
          images.push(match[1]);
        } else {
          files.push(match[1]);
        }
      } else {
        textLines.push(line);
      }
    }
    return { textContent: textLines.join('\n').trim(), imagePaths: images, filePaths: files };
  }, [message.content]);

  // 新上传保存的是 hostinput 标识，预览与下载必须走输入存储接口；历史主机路径保持兼容。
  const buildReadUrl = useCallback((filePath: string) => {
    return buildAttachmentAccessUrl(filePath, sessionId);
  }, [sessionId]);

  // Load image thumbnails via API
  useEffect(() => {
    if (imagePaths.length === 0) return;
    let cancelled = false;
    (async () => {
      const results: Record<string, string> = {};
      for (const p of imagePaths) {
        if (imageDataURLs[p]) continue;
        try {
          if (desktopTargetClientId) {
            const result = await relayService.readImage(desktopTargetClientId, p, 1200, 80);
            if (!cancelled && result.content && result.mimeType) {
              results[p] = `data:${result.mimeType};base64,${result.content}`;
            }
            continue;
          }
          const resp = await fetch(buildReadUrl(p), { headers: getAuthHeaders() });
          if (!resp.ok) continue;
          const data = await resp.json();
          if (!cancelled && data.type === 'image' && data.dataUrl) {
            results[p] = data.dataUrl;
          }
        } catch {
          // ignore read errors
        }
      }
      if (!cancelled && Object.keys(results).length > 0) {
        setImageDataURLs(prev => ({ ...prev, ...results }));
      }
    })();
    return () => { cancelled = true; };
  }, [imagePaths, buildReadUrl, desktopTargetClientId]);

  // Preview file handler
  const handlePreview = useCallback(async (filePath: string) => {
    setPreviewFile(filePath);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      if (desktopTargetClientId && IMAGE_EXTS.some(ext => filePath.toLowerCase().endsWith(ext))) {
        const result = await relayService.readImage(desktopTargetClientId, filePath, 2400, 90);
        setPreviewData({
          type: 'image',
          dataUrl: `data:${result.mimeType};base64,${result.content}`,
          fileName: filePath.split(/[\\/]/).pop() || filePath,
        });
        return;
      }
      if (desktopTargetClientId) {
        const result = await relayService.readFile(desktopTargetClientId, filePath);
        const fileName = filePath.split(/[\\/]/).pop() || filePath;
        if (result.encoding === 'utf-8') {
          setPreviewData({ type: 'text', content: result.content, fileName });
        } else if (result.encoding === 'base64') {
          setPreviewData({
            type: 'binary',
            fileName,
            size: result.size,
            downloadUrl: `data:${result.mimeType || 'application/octet-stream'};base64,${result.content}`,
          });
        } else {
          setPreviewData({ type: 'error', message: '不支持的文件编码' });
        }
        return;
      }
      const resp = await fetch(buildReadUrl(filePath), { headers: getAuthHeaders() });
      if (!resp.ok) {
        setPreviewData({ type: 'error', message: `读取失败 (${resp.status})` });
        return;
      }
      const data = await resp.json();
      setPreviewData(data.type === 'binary'
        ? { ...data, downloadUrl: buildAttachmentAccessUrl(filePath, sessionId, 'download') }
        : data as FilePreviewData);
    } catch (err: any) {
      setPreviewData({ type: 'error', message: err.message || '读取失败' });
    } finally {
      setPreviewLoading(false);
    }
  }, [buildReadUrl, desktopTargetClientId, sessionId]);

  const closePreview = useCallback(() => {
    setPreviewFile(null);
    setPreviewData(null);
  }, []);

  const hasAttachments = imagePaths.length > 0 || filePaths.length > 0;

  return (
    <div
      className="py-2 px-4"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="max-w-3xl mx-auto">
        <div className="pl-4 sm:pl-8 md:pl-12">
          <div className="flex items-start gap-3 flex-row-reverse">
            <div className="w-full min-w-0 flex flex-col items-end">
              {/* Text bubble */}
              {textContent && (
                <div className="w-fit max-w-[42rem] rounded-2xl px-4 py-2.5 dark:bg-claude-darkSurface bg-claude-surface dark:text-claude-darkText text-claude-text shadow-subtle">
                  <MarkdownContent
                    content={textContent}
                    className="max-w-none whitespace-pre-wrap break-words"
                  />
                </div>
              )}
              {/* Attachment cards — shown below the text bubble */}
              {hasAttachments && (
                <div className={`flex flex-wrap gap-2 justify-end ${textContent ? 'mt-2' : ''}`}>
                  {filePaths.map((p, i) => (
                    <AttachmentFileCard
                      key={`file-${i}`}
                      filePath={p}
                      isImage={false}
                      readUrl={buildReadUrl(p)}
                      onPreview={handlePreview}
                    />
                  ))}
                  {imagePaths.map((p, i) => (
                    <AttachmentFileCard
                      key={`img-${i}`}
                      filePath={p}
                      isImage={true}
                      imageDataURL={imageDataURLs[p]}
                      readUrl={buildReadUrl(p)}
                      onPreview={handlePreview}
                    />
                  ))}
                </div>
              )}
              {/* 技能标签最多展示 3 个，其余折叠成 "+N"，并允许换行，避免协作模式下
                  一次挂载大量技能时把这一行撑爆（移动端尤其明显）。 */}
              <div className="flex flex-wrap items-center justify-end gap-1.5 mt-1 max-w-full">
                {messageSkills.slice(0, 3).map(skill => (
                  <div
                    key={skill.id}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-claude-accent/5 dark:bg-claude-accent/10 shrink-0"
                    title={skill.description}
                  >
                    <SkillIcon className="h-2.5 w-2.5 text-claude-accent/70" />
                    <span className="text-[10px] font-medium text-claude-accent/70 max-w-[60px] truncate">
                      {skill.name}
                    </span>
                  </div>
                ))}
                {messageSkills.length > 3 && (
                  <div
                    className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-claude-accent/5 dark:bg-claude-accent/10 shrink-0"
                    title={messageSkills.slice(3).map(s => s.name).join('、')}
                  >
                    <span className="text-[10px] font-medium text-claude-accent/70">
                      +{messageSkills.length - 3}
                    </span>
                  </div>
                )}
                <CopyButton
                  content={textContent}
                  visible={isHovered}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* File preview modal */}
      {previewFile && (
        <FilePreviewModal
          filePath={previewFile}
          data={previewData}
          loading={previewLoading}
          onClose={closePreview}
        />
      )}
    </div>
  );
};

const AssistantMessageItem: React.FC<{
  message: CoworkMessage;
  /** 所属会话的鉴权图片读取器。 */
  loadImage?: MarkdownImageLoader;
  /** 本轮已由卡片展示的文件，供所有正文分支去重。 */
  registeredArtifactPaths?: string[];
  resolveLocalFilePath?: (href: string, text: string) => string | null;
  mapDisplayText?: (value: string) => string;
  showThinking?: boolean;
  onFileClick?: (filePath: string) => void;
}> = ({
  message,
  loadImage,
  registeredArtifactPaths,
  resolveLocalFilePath,
  mapDisplayText,
  showThinking = true,
  onFileClick,
}) => {
  const rawContent = mapDisplayText ? mapDisplayText(message.content) : message.content;
  const displayContent = stripThinkingLeak(rawContent);
  const thinkingSegments = splitThinkingLeakSegments(rawContent).filter(segment => segment.type === 'thinking');
  const isStreaming = Boolean(message.metadata?.isStreaming);
  const hasWidgetFence = /```show-widget/.test(displayContent);

  const renderWidgetContent = (content: string, streaming: boolean) => {
    if (streaming) {
      // Check if last fence is closed
      const lastFenceStart = content.lastIndexOf('```show-widget');
      if (lastFenceStart !== -1) {
        const afterLastFence = content.slice(lastFenceStart + '```show-widget'.length);
        const fenceClosedRegex = /[\s\S]*?```/;
        const isClosed = fenceClosedRegex.test(afterLastFence);

        if (isClosed) {
          // All fences closed — render normally
          const segments = parseAllShowWidgets(content);
          return segments.map((seg, i) =>
            seg.type === 'text'
              ? <MarkdownContent key={`t-${i}`} content={seg.content} className="prose dark:prose-invert max-w-none" resolveLocalFilePath={resolveLocalFilePath} onFileClick={onFileClick} loadImage={loadImage} registeredArtifactPaths={registeredArtifactPaths} />
              : <WidgetRenderer key={`w-${i}`} widgetCode={seg.data.widget_code} isStreaming={true} title={seg.data.title} />
          );
        }

        // Last fence not closed — render completed segments + partial widget
        const completedPart = content.slice(0, lastFenceStart);
        const partialFenceBody = afterLastFence.trim();

        const completedSegments = completedPart.trim() ? parseAllShowWidgets(completedPart) : [];
        const rendered = completedSegments.map((seg, i) =>
          seg.type === 'text'
            ? <MarkdownContent key={`t-${i}`} content={seg.content} className="prose dark:prose-invert max-w-none" resolveLocalFilePath={resolveLocalFilePath} onFileClick={onFileClick} loadImage={loadImage} registeredArtifactPaths={registeredArtifactPaths} />
            : <WidgetRenderer key={`w-${i}`} widgetCode={seg.data.widget_code} isStreaming={false} title={seg.data.title} />
        );

        // Try extracting partial widget code
        const partialWidget = extractTruncatedWidget(partialFenceBody);
        if (partialWidget) {
          const partialKey = computePartialWidgetKey(content);
          rendered.push(
            <WidgetRenderer key={partialKey} widgetCode={partialWidget.widget_code} isStreaming={true} title={partialWidget.title} showOverlay={CDN_PATTERN_WIDGET.test(partialWidget.widget_code)} />
          );
        }

        return rendered;
      }
    }

    // Non-streaming: all fences completed
    const segments = parseAllShowWidgets(content);
    return segments.map((seg, i) =>
      seg.type === 'text'
        ? <MarkdownContent key={`t-${i}`} content={seg.content} className="prose dark:prose-invert max-w-none" resolveLocalFilePath={resolveLocalFilePath} onFileClick={onFileClick} loadImage={loadImage} registeredArtifactPaths={registeredArtifactPaths} />
        : <WidgetRenderer key={`w-${i}`} widgetCode={seg.data.widget_code} isStreaming={false} title={seg.data.title} />
    );
  };

  return (
    <div className="relative">
      {showThinking && thinkingSegments.map((segment, index) => <ThinkingBlock key={index} message={{ ...message, content: segment.content }} />)}
      <div className="dark:text-claude-darkText text-claude-text">
        {hasWidgetFence ? (
          <>{renderWidgetContent(displayContent, isStreaming)}</>
        ) : (
          <MarkdownContent
            content={displayContent}
            className="prose dark:prose-invert max-w-none"
            loadImage={loadImage}
            registeredArtifactPaths={registeredArtifactPaths}
            resolveLocalFilePath={resolveLocalFilePath}
            onFileClick={onFileClick}
          />
        )}
      </div>

    </div>
  );
};

/** CDN pattern for detecting scripts that need loading overlay during streaming. */
const CDN_PATTERN_WIDGET = /cdnjs\.cloudflare\.com|cdn\.jsdelivr\.net|unpkg\.com|esm\.sh/;

const ThinkingBlock: React.FC<{
  message: CoworkMessage;
  mapDisplayText?: (value: string) => string;
}> = ({ message, mapDisplayText }) => {
  const isCurrentlyStreaming = Boolean(message.metadata?.isStreaming);
  // 默认收起，点击 caret 才展开。流式中也不自动展开，避免遮挡正文与频繁回流。
  const [isExpanded, setIsExpanded] = useState(false);
  const displayContent = (mapDisplayText ? mapDisplayText(message.content) : message.content).trim();

  return (
    <div data-thinking-block="true" className="rounded-lg border dark:border-claude-darkBorder/50 border-claude-border/50 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left dark:hover:bg-claude-darkSurfaceHover/50 hover:bg-claude-surfaceHover/50 transition-colors"
      >
        <ChevronRightIcon
          className={`h-3.5 w-3.5 dark:text-claude-darkTextSecondary text-claude-textSecondary flex-shrink-0 transition-transform duration-200 ${
            isExpanded ? 'rotate-90' : ''
          }`}
        />
        <span className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
          {i18nService.t('reasoning')}
        </span>
        {isCurrentlyStreaming && (
          <span className="w-1.5 h-1.5 rounded-full bg-claude-accent animate-pulse" />
        )}
      </button>
      {isExpanded && (
        <div className="px-3 pb-3 max-h-64 overflow-y-auto">
          <div className="text-xs leading-relaxed dark:text-claude-darkTextSecondary/80 text-claude-textSecondary/80 whitespace-pre-wrap">
            {displayContent}
          </div>
        </div>
      )}
    </div>
  );
};

const AssistantTurnBlock: React.FC<{
  turn: ConversationTurn;
  credit?: SessionCreditSnapshot | null;
  searchFocus?: number;
  searchMessageId?: string;
  showThinking?: boolean;
  sessionId: string;
  desktop: boolean;
  /** 向同一回合中的全部 Markdown 答复透传图片读取范围。 */
  loadImage?: MarkdownImageLoader;
  resolveLocalFilePath?: (href: string, text: string) => string | null;
  mapDisplayText?: (value: string) => string;
  showTypingIndicator?: boolean;
  showCopyButtons?: boolean;
  isTurnRunning?: boolean;
  onFileClick?: (filePath: string) => void;
  recoveryEnabled?: boolean;
  onContinue?: (prompt: string) => void | boolean | Promise<void | boolean>;
}> = ({
  turn,
  credit = null,
  searchFocus = 0,
  searchMessageId = '',
  showThinking = true,
  sessionId,
  desktop,
  loadImage,
  resolveLocalFilePath,
  mapDisplayText,
  showTypingIndicator = false,
  showCopyButtons = true,
  isTurnRunning = false,
  recoveryEnabled = false,
  onFileClick,
  onContinue,
}) => {
  const summary = readTurnSummary(turn.userMessage);
  const visibleAssistantItems = getVisibleAssistantItems(turn.assistantItems).filter(item =>
    showThinking || item.type !== 'assistant' || !item.message.metadata?.isThinking);
  const activitySections = buildActivitySections(visibleAssistantItems, isTurnRunning);
  const executionPhase = conversationExecutionPhase({
    running: isTurnRunning,
    hasAnswer: visibleAssistantItems.some(item => item.type === 'assistant' && !item.message.metadata?.isThinking && Boolean(item.message.content.trim())),
    hasThinking: visibleAssistantItems.some(item => item.type === 'assistant' && item.message.metadata?.isThinking === true && Boolean(item.message.content.trim())),
    hasTools: visibleAssistantItems.some(item => item.type === 'tool_group' || item.type === 'tool_result'),
  });
  const showExecutionBrand = visibleAssistantItems.length > 0 || showTypingIndicator || isTurnRunning;
  const pendingTool = [...visibleAssistantItems].reverse().find(item => item.type === 'tool_group' && !item.group.toolResult);
  const activityLabel = pendingTool?.type === 'tool_group'
    ? getToolActionLabel(String(pendingTool.group.toolUse.metadata?.toolName ?? 'Tool'), pendingTool.group.toolUse.metadata?.toolInput)
    : undefined;
  // 一轮只显示最后一份清单，不把会话级的历史登记文件重复挂到每条回复上。
  const manifestEntry = [...turn.assistantItems].reverse().find(item => item.type === 'assistant' && readResultManifest(item.message.metadata?.resultManifest));
  const resultManifest = manifestEntry?.type === 'assistant' ? readResultManifest(manifestEntry.message.metadata?.resultManifest) : undefined;
  const registeredArtifactPaths = resultManifest?.artifacts.map(artifact => artifact.path);

  // 子 Agent 区域折叠状态:未手动切换时跟随完成态(运行中展开/完成收起)
  const [subagentCollapsed, setSubagentCollapsed] = React.useState<Record<string, boolean>>({});

  const renderSystemMessage = (message: CoworkMessage) => {
    const rawContent = hasText(message.content)
      ? message.content
      : (typeof message.metadata?.error === 'string' ? message.metadata.error : '');
    let content = mapDisplayText ? mapDisplayText(rawContent) : rawContent;
    const userError = message.metadata?.userError;
    if (userError) {
      const latestError = [...turn.assistantItems].reverse().find(item => item.type === 'system' && item.message.metadata?.userError);
      return <CoworkUserErrorCard key={`${sessionId}:${userError.errorId}`} error={userError} sessionId={sessionId}
        actionable={recoveryEnabled && latestError?.type === 'system' && latestError.message.id === message.id} onContinue={onContinue} />;
    }
    if (!content.trim()) return null;

    // 云端沙箱启动状态：服务端只落 epoch(sandboxLifecycle)，时刻在这里按观看者本地
    // 时区渲染并拼到文案后，保证与任务完成 pill 同一口径。老消息没有该 metadata、
    // 时刻已烤在 content 里，走原样渲染，不会重复追加。
    const sbx = message.metadata?.sandboxLifecycle;
    if (sbx && sbx.state !== 'starting' && typeof sbx.finishedAt === 'number') {
      const label = sbx.state === 'ok' ? '已完成' : '已失败';
      content = `${content}（${formatCompletionMeta(sbx.finishedAt, sbx.elapsedMs ?? 0, label)}）`;
    }

    // 会话内切换模型：渲染成一条居中的细分隔线（左右横线 + 齿轮图标 + 弱化文案），
    // 与普通系统消息的卡片区别开 —— 它只是一条状态旁注，不该抢正文的视觉权重。
    // 用户改下拉框时服务端会立刻推这条 message，进行中的会话也能实时看到。
    const modelSwitch = message.metadata?.modelSwitch as
      { from: string; to: string; effective?: 'next-turn' | 'immediate' } | undefined;
    if (modelSwitch) {
      const effect = modelSwitch.effective === 'immediate' ? '立即生效' : '下次对话生效';
      return (
        <div className="flex items-center gap-3 py-1 select-none">
          <div className="h-px flex-1 dark:bg-claude-darkBorder bg-claude-border" />
          <div className="flex items-center gap-1.5 text-[11px] leading-none whitespace-nowrap dark:text-claude-darkTextSecondary text-claude-textSecondary">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 opacity-70">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            <span>模型已从 {modelSwitch.from} 更改为 {modelSwitch.to}</span>
            <span className="opacity-60">· {effect}</span>
          </div>
          <div className="h-px flex-1 dark:bg-claude-darkBorder bg-claude-border" />
        </div>
      );
    }

    // 模型不可用：服务端以 MODEL_UNAVAILABLE 拒绝了这一轮，请求没有到达执行器，
    // 所以不会有任何流事件回来。渲染成醒目的错误卡片并列出该用户当前真正可用的模型，
    // 否则用户只会看到"消息发出去了但没有任何反应"，无从判断该怎么恢复。
    const modelUnavailable = message.metadata?.modelUnavailable as
      { modelId: string; available: string[] } | undefined;
    if (modelUnavailable) {
      const available = Array.isArray(modelUnavailable.available) ? modelUnavailable.available : [];
      return (
        <div className="my-2 rounded-lg border px-3 py-2.5 text-[13px] leading-relaxed dark:border-red-900/50 dark:bg-red-950/20 border-red-200 bg-red-50">
          <div className="flex items-start gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 flex-shrink-0 text-red-500">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div className="min-w-0 flex-1">
              <div className="dark:text-red-200 text-red-800">{content}</div>
              {available.length > 0 && (
                <div className="mt-1.5 dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  <span>请在下方输入框的模型选择器中改用：</span>
                  <span className="ml-1 break-all font-medium dark:text-claude-darkText text-claude-text">
                    {available.join('、')}
                  </span>
                </div>
              )}
              {available.length === 0 && (
                <div className="mt-1.5 dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  当前没有可用模型，请联系管理员检查模型渠道配置。
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // WEB-12：文档工具生成的 Host 托管产物（消息持久化，刷新/沙箱暂停后仍可下载）
    const documentArtifact = message.metadata?.documentArtifact as
      | { artifactRef: string; fileName: string; byteLength?: number; downloadUrl: string }
      | undefined;
    if (documentArtifact?.artifactRef && documentArtifact.fileName
      && registeredArtifactPaths?.includes(documentArtifactPath(documentArtifact.artifactRef, documentArtifact.fileName) ?? '')) return null;
    if (documentArtifact && isArtifactDownloadUrl(documentArtifact.downloadUrl)) {
      const handleDocumentDownload = async () => {
        const headers: Record<string, string> = {};
        const token = localStorage.getItem('boclaw_token');
        const userId = localStorage.getItem('boclaw_user_id');
        const role = localStorage.getItem('boclaw_user_role');
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (userId) headers['X-Boclaw-User-Id'] = userId;
        if (role) headers['X-Boclaw-User-Role'] = role;
        const blob = await fetchDeliverableArtifactBlob(documentArtifact.downloadUrl, headers);
        if (!blob) {
          window.dispatchEvent(new CustomEvent('app:showToast', { detail: '下载失败：文件不存在、已过期或无权访问' }));
          return;
        }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = documentArtifact.fileName || 'document';
        a.click();
        URL.revokeObjectURL(url);
      };
      const sizeText = typeof documentArtifact.byteLength === 'number'
        ? ` · ${documentArtifact.byteLength >= 1024 * 1024 ? `${(documentArtifact.byteLength / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(documentArtifact.byteLength / 1024))} KB`}`
        : '';
      return (
        <div className="rounded-lg border dark:border-claude-accent/30 border-claude-accent/30 dark:bg-claude-accent/5 bg-claude-accent/5 px-3 py-2.5" data-document-artifact={documentArtifact.artifactRef}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <InformationCircleIcon className="h-4 w-4 text-claude-accent flex-shrink-0" />
              <span className="text-xs dark:text-claude-darkText text-claude-text truncate">
                文档已生成 <strong>{documentArtifact.fileName}</strong>{sizeText}
              </span>
            </div>
            <button
              onClick={() => { void handleDocumentDownload(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-claude-accent text-white hover:bg-claude-accent/90 transition-colors flex-shrink-0"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              下载
            </button>
          </div>
        </div>
      );
    }

    // 技能下载消息：渲染带下载按钮的卡片
    const skillDownload = message.metadata?.skillDownload as { skillId: string; downloadUrl: string } | undefined;
    if (skillDownload) {
      const handleDownload = async () => {
        try {
          // 构建认证 headers
          const headers: Record<string, string> = {};
          const token = localStorage.getItem('boclaw_token');
          const userId = localStorage.getItem('boclaw_user_id');
          const role = localStorage.getItem('boclaw_user_role');
          if (token) headers['Authorization'] = `Bearer ${token}`;
          if (userId) headers['X-Boclaw-User-Id'] = userId;
          if (role) headers['X-Boclaw-User-Role'] = role;
          const resp = await fetch(skillDownload.downloadUrl, { headers });
          if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${skillDownload.skillId}.zip`;
          a.click();
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error('Skill download failed:', err);
          window.dispatchEvent(new CustomEvent('app:showToast', { detail: '下载失败，请确认沙箱仍在运行' }));
        }
      };
      return (
        <div className="rounded-lg border dark:border-claude-accent/30 border-claude-accent/30 dark:bg-claude-accent/5 bg-claude-accent/5 px-3 py-2.5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <InformationCircleIcon className="h-4 w-4 text-claude-accent flex-shrink-0" />
              <span className="text-xs dark:text-claude-darkText text-claude-text">
                技能 <strong>{skillDownload.skillId}</strong> 已创建/更新
              </span>
            </div>
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-claude-accent text-white hover:bg-claude-accent/90 transition-colors flex-shrink-0"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              下载技能包
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="rounded-lg border dark:border-claude-darkBorder/70 border-claude-border/70 dark:bg-claude-darkBg/40 bg-claude-bg/60 px-3 py-2">
        <div className="flex items-start gap-2">
          <InformationCircleIcon className="h-4 w-4 mt-0.5 dark:text-claude-darkTextSecondary text-claude-textSecondary flex-shrink-0" />
          <div className="text-xs whitespace-pre-wrap dark:text-claude-darkTextSecondary text-claude-textSecondary">
            {content}
          </div>
        </div>
      </div>
    );
  };

  const renderOrphanToolResult = (message: CoworkMessage) => {
    const toolResultDisplayRaw = getToolResultDisplay(message);
    const toolResultDisplay = mapDisplayText ? mapDisplayText(toolResultDisplayRaw) : toolResultDisplayRaw;
    const isToolError = Boolean(message.metadata?.isError || message.metadata?.error);
    const resultLineCount = getToolResultLineCount(toolResultDisplay);
    return (
      <div className="py-1">
        <div className="flex items-start gap-2">
          <span className={`mt-[3px] w-3 h-3 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[7px] font-bold leading-none ${
            isToolError ? 'bg-gray-400 dark:bg-gray-500' : 'bg-green-500'
          }`}>
            {isToolError ? '✕' : '✓'}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {i18nService.t('coworkToolResult')}
            </div>
            {resultLineCount > 0 && (
              <div className="text-xs dark:text-claude-darkTextSecondary/60 text-claude-textSecondary/60 mt-0.5">
                {resultLineCount} {resultLineCount === 1 ? 'line' : 'lines'} of output
              </div>
            )}
            <div className="mt-2 px-3 py-2 rounded-lg dark:bg-claude-darkSurface/50 bg-claude-surface/50 max-h-64 overflow-y-auto">
              <pre className={`text-xs whitespace-pre-wrap break-words font-mono ${
                isToolError ? 'text-red-500' : 'dark:text-claude-darkText text-claude-text'
              }`}>
                {toolResultDisplay || i18nService.t('coworkToolRunning')}
              </pre>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAssistantItem = (item: AssistantTurnItem) => {
    if (item.type === 'steering') return <div key={item.message.id} data-message-id={item.message.id}>
      <p className="mb-1 text-right text-xs text-claude-textSecondary dark:text-claude-darkTextSecondary">{i18nService.t('coworkQueuedSteer')}</p>
      <UserMessageItem message={item.message} skills={[]} sessionId={sessionId} />
    </div>;
    if (item.type === 'assistant') {
      if (item.message.metadata?.isThinking) {
        if (!showThinking) return null;
        return (
          <ThinkingBlock
            key={item.message.id}
            message={item.message}
            mapDisplayText={mapDisplayText}
          />
        );
      }
      return (
        <div key={item.message.id} data-message-id={item.message.id}><AssistantMessageItem
          message={item.message}
          loadImage={loadImage}
          registeredArtifactPaths={registeredArtifactPaths}
          resolveLocalFilePath={resolveLocalFilePath}
          mapDisplayText={mapDisplayText}
          showThinking={showThinking}
          onFileClick={onFileClick}
        /></div>
      );
    }

    if (item.type === 'tool_group') {
      return (
        <div key={`tool-${item.group.toolUse.id}`} data-message-id={item.group.toolUse.id} data-result-message-id={item.group.toolResult?.id}>
          <ToolCallGroup group={item.group} mapDisplayText={mapDisplayText}
            searchFocus={searchMessageId === item.group.toolUse.id || searchMessageId === item.group.toolResult?.id ? searchFocus : 0} />
        </div>
      );
    }

    if (item.type === 'system') {
      const systemMessage = renderSystemMessage(item.message);
      if (!systemMessage) {
        return null;
      }
      return (
        <div key={item.message.id}>
          {systemMessage}
        </div>
      );
    }

    return (
      <div key={item.message.id}>
        {renderOrphanToolResult(item.message)}
      </div>
    );
  };

  /** 用消息 ID 定位调用或结果，展开对应过程和子代理，不依赖重新分组的下标。 */
  const containsSearch = (entries: IndexedAssistantItem[]): boolean => searchFocus > 0 && entries.some(({ item }) =>
    item.type === 'tool_group' ? item.group.toolUse.id === searchMessageId || item.group.toolResult?.id === searchMessageId
      : item.message.id === searchMessageId);

  /** 工具序列使用首调用作为稳定标识，摘要跟随当前尚未结束的调用。 */
  const renderSequences = (entries: IndexedAssistantItem[]) => buildToolSequences(entries).map(group => {
    if (!group.sequence) return renderAssistantItem(group.entries[0].item);
    const tools = group.entries.flatMap(({ item }) => item.type === 'tool_group' ? [item.group] : []);
    const current = isTurnRunning ? tools.slice().reverse().find(tool => !tool.toolResult) ?? tools[0] : tools[0];
    const name = typeof current.toolUse.metadata?.toolName === 'string' ? current.toolUse.metadata.toolName : 'Tool';
    const rawLabel = getToolActionLabel(name, current.toolUse.metadata?.toolInput);
    return <ConversationActivityDisclosure key={`sequence-${group.key}`} kind="sequence"
      label={mapDisplayText ? mapDisplayText(rawLabel) : rawLabel} toolCount={tools.length}
      running={isTurnRunning && tools.some(tool => !tool.toolResult)}
      searchFocus={containsSearch(group.entries) ? searchFocus : 0}>
      {group.entries.map(({ item }) => renderAssistantItem(item))}
    </ConversationActivityDisclosure>;
  });

  /** 子代理是工具序列的作用域边界；不能将其前后的主代理步骤合并。 */
  const renderActivityEntries = (entries: IndexedAssistantItem[]) => {
    const rendered: React.ReactNode[] = [];
    let rootEntries: IndexedAssistantItem[] = [];
    /** 遇到子代理边界时提交主代理的连续工具段。 */
    const flushRoot = () => { rendered.push(...renderSequences(rootEntries)); rootEntries = []; };
    for (const entry of buildSubagentRenderPlan(entries)) {
      if (entry.kind === 'item') { rootEntries.push(entry); continue; }
      flushRoot();
      const collapsed = containsSearch(entry.entries) ? false : (subagentCollapsed[entry.parentToolUseId] ?? (entry.completed && !entry.entries.some(({ item }) => isActivityFailure(item))));
      rendered.push(<SubagentSectionCard key={`subagent-${entry.parentToolUseId}`} label={entry.label}
        completed={entry.completed} stepCount={entry.entries.length} collapsed={collapsed}
        onToggle={() => setSubagentCollapsed(previous => ({ ...previous, [entry.parentToolUseId]: !collapsed }))}>
        {renderSequences(entry.entries)}
      </SubagentSectionCard>);
    }
    flushRoot();
    return rendered;
  };

  return (
    <div className="px-4 py-2">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0 px-4 py-2 space-y-0.5">
            {showExecutionBrand && <div className="mb-2 flex h-[26px] items-center" data-execution-brand><BomateWordmark className="h-[18px] w-[68px] translate-y-px" /></div>}
            {executionPhase !== 'thinking' && activitySections.map(section => section.process
              ? <ConversationActivityDisclosure key={`process-${section.key}`} kind="process"
                  toolCount={section.entries.filter(({ item }) => item.type === 'tool_group' || item.type === 'tool_result').length}
                  searchFocus={containsSearch(section.entries) ? searchFocus : 0}>
                  {renderActivityEntries(section.entries)}
                </ConversationActivityDisclosure>
              : <React.Fragment key={`visible-${section.key}`}>{renderActivityEntries(section.entries)}</React.Fragment>)}
            <ResultManifestCards manifest={resultManifest} loadImage={loadImage} onFileClick={onFileClick} />
            <ConversationExecutionStatus phase={executionPhase} activityLabel={activityLabel} startedAt={turn.userMessage?.timestamp} />
            {!desktop && !isTurnRunning && turn.userMessage && <ConversationResults key={`results-${sessionId}:${turn.userMessage.id}`} sessionId={sessionId} turnId={turn.userMessage.id} />}
            <ConversationTurnFooter key={`${sessionId}:${turn.id}`} turnId={turn.id} sessionId={sessionId}
              desktop={desktop} sections={activitySections} summary={summary} credit={credit}
              turnIds={turn.userMessage?.metadata?.billingTurnIds ?? []} running={isTurnRunning}
              showActions={showCopyButtons} mapDisplayText={mapDisplayText} />
          </div>
        </div>
      </div>
    </div>
  );
};

const CoworkSessionDetail: React.FC<CoworkSessionDetailProps> = ({
  onManageSkills,
  onContinue,
  onStop,
  onNavigateHome,
  isSidebarCollapsed,
  onToggleSidebar,
  onNewChat,
  isMobile,
  overrideSandboxProfileId,
  onSandboxProfileOverride,
  overrideSandboxImageId,
  onSandboxImageOverride,
  activeScenario,
  onSelectScenario,
  onDismissScenario,
}) => {
  const isMac = window.electron.platform === 'darwin';
  const dispatch = useDispatch();
  const currentSession = useSelector((state: RootState) => state.cowork.currentSession);
  const [showThinking, toggleThinking] = useThinkingPreference();
  const [searchOpen, setSearchOpen] = useState(false);
  const [memoryFilesOpen, setMemoryFilesOpen] = useState(false);
  useEffect(() => setMemoryFilesOpen(false), [currentSession?.id]);
  const [searchTarget, setSearchTarget] = useState({ turnId: '', messageId: '', serial: 0 });
  useEffect(() => { setSearchOpen(false); setSearchTarget({ turnId: '', messageId: '', serial: 0 }); }, [currentSession?.id]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'f') {
        event.preventDefault(); setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  const sessionCredit = useSessionCredit(currentSession?.id, currentSession?.status);
  const isStreaming = useSelector((state: RootState) => state.cowork.isStreaming);
  const queuedTurnCount = useSelector((state: RootState) => (state.cowork.queuedMessagesBySession[currentSession?.id ?? ''] ?? []).length);
  const skills = useSelector((state: RootState) => state.skill.skills);
  const allPendingPermissions = useSelector((state: RootState) => state.cowork.pendingPermissions);
  const registeredDeliverables = useSelector(
    (state: RootState) => state.cowork.registeredDeliverables[currentSession?.id ?? ''] ?? []
  );
  const pendingPermission = useMemo(
    () => allPendingPermissions.find((p) => p.sessionId === currentSession?.id) ?? null,
    [allPendingPermissions, currentSession?.id]
  );
  const detailRootRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { containerRef: scrollContainerRef, contentRef: messagesContentRef, following: shouldAutoScroll,
    setFollowing: setShouldAutoScroll, onScroll: handleMessagesScroll } = useConversationScroll(currentSession?.id);
  const promptInputRef = useRef<CoworkPromptInputRef>(null);

  // Sandbox profiles & images for displaying (disabled) selectors in session detail
  const [sandboxProfiles, setSandboxProfiles] = useState<SandboxProfile[]>([]);
  const [sandboxImages, setSandboxImages] = useState<SandboxImage[]>([]);

  // 会话已带着自己的 (profile, image) 建立时只认会话值；覆盖值只对还没定环境的会话生效。
  const composerSandbox = resolveSessionSandboxSelection(currentSession, {
    profileId: overrideSandboxProfileId,
    imageId: overrideSandboxImageId,
  });

  useEffect(() => {
    coworkService.getSandboxProfiles().then(setSandboxProfiles).catch(() => {});
    coworkService.getSandboxImageCatalog().then(setSandboxImages).catch(() => {});
  }, []);

  // 全局拖拽文件状态
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const pageDragDepthRef = useRef(0);

  // 使用 document 级原生事件监听，确保能拦截浏览器默认的文件打开行为
  useEffect(() => {
    const isFileDrag = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');

    const onDragEnter = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      pageDragDepthRef.current++;
      if (pageDragDepthRef.current === 1) setIsDraggingOver(true);
    };

    const onDragOver = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };

    const onDragLeave = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      pageDragDepthRef.current = Math.max(0, pageDragDepthRef.current - 1);
      if (pageDragDepthRef.current === 0) setIsDraggingOver(false);
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      pageDragDepthRef.current = 0;
      setIsDraggingOver(false);
      if ((e as any)._boclawFileHandled) return;
      if (e.dataTransfer?.files.length) {
        promptInputRef.current?.addFiles(e.dataTransfer.files);
      }
    };

    document.addEventListener('dragenter', onDragEnter);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('drop', onDrop);
    };
  }, []);

  // VNC state
  const [vncUrl, setVncUrl] = useState<string | null>(null);
  // 一次性 bootstrap ticket 只进入 vnc.html；WebSocket 仅使用服务端落下的 HttpOnly grant cookie。
  const [vncToken, setVncToken] = useState<string | null>(null);
  const vncInstanceRef = useRef<string | null>(null);
  const [showVncModal, setShowVncModal] = useState(false);
  // 沙箱预览停靠条(右侧面板底部,与终端同面板);可被关闭,顶栏按钮重开
  const [showVncDock, setShowVncDock] = useState(true);
  const vncPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [vncSplitRatio, setVncSplitRatio] = useState(0.7);
  const vncDragRef = useRef<{ startX: number; startRatio: number } | null>(null);
  const vncIframeRef = useRef<HTMLIFrameElement | null>(null);
  const splitContainerRef = useRef<HTMLDivElement | null>(null);
  const vncThumbnailRef = useRef<HTMLDivElement | null>(null);
  const vncPanelRef = useRef<HTMLDivElement | null>(null);

  // Persistent VNC iframe — created once via DOM, wrapped in an overflow:hidden
  // div that lives on document.body. The wrapper is moved/resized via
  // position:fixed inline styles to overlay the active host rect; the iframe
  // itself keeps a fixed native size (4:3) and is CSS-scaled inside the wrapper
  // to either "cover" (thumbnail — crops the empty desktop area, no letterbox)
  // or "contain" (modal — shows the full remote). iframe DOM parent never
  // changes, so noVNC never reconnects.
  // 16:9 is the most common modern VNC/desktop aspect ratio. Matching it here
  // means noVNC rarely needs to letterbox inside the iframe, which would
  // otherwise leak as black bars on top/bottom that our cover-scale can't hide.
  const VNC_NATIVE_W = 1280;
  const VNC_NATIVE_H = 720;
  const vncIframeDomRef = useRef<HTMLIFrameElement | null>(null);
  const vncIframeWrapRef = useRef<HTMLDivElement | null>(null);
  const vncFloatingHostRef = useRef<HTMLDivElement | null>(null);

  // Build proxied VNC viewer URL (injects CSS to hide sidebar/notification)
  const vncViewerUrl = useMemo(() => {
    if (!vncUrl || !currentSession) return null;
    try {
      return buildVncViewerUrl({
        sessionId: currentSession.id,
        ticket: vncToken,
      });
    } catch {
      return null;
    }
  }, [vncUrl, vncToken, currentSession?.id]);

  // Deliverables panel state
  const [showDeliverables, setShowDeliverables] = useState(false);
  const [deliverableHighlightPath, setDeliverableHighlightPath] = useState<string | null>(null);

  // Terminal panel state. Terminal is only available once a sandbox (E2B) is
  // live — we reuse `vncUrl` (set once the sandbox container is ready) as the gate.
  const [showTerminal, setShowTerminal] = useState(false);

  /** Callback passed to MarkdownContent: open deliverables panel and highlight file */
  const handleFileClickFromMarkdown = useCallback((filePath: string) => {
    setDeliverableHighlightPath(filePath);
    setShowDeliverables(true);
  }, []);

  // TodoWrite panel state
  const [todoPanelDismissed, setTodoPanelDismissed] = useState(false);
  const todoDismissedAtRef = useRef<number>(0);
  const allMessages = currentSession?.messages || [];
  /** 搜索与后台任务共用稳定锚点定位，避免重复维护展开/滚动逻辑。 */
  const locateConversationMessage = (turnId: string, messageId: string) => {
        setSearchTarget(old => ({ turnId, messageId, serial: old.serial + 1 }));
        setShouldAutoScroll(false);
        // 外层过程与内层工具会依次展开，两帧后再按稳定消息 ID 定位。
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const nodes = [...(scrollContainerRef.current?.querySelectorAll<HTMLElement>('[data-message-id], [data-result-message-id], [data-turn-id]') ?? [])];
          const node = nodes.find(item => item.dataset.messageId === messageId || item.dataset.resultMessageId === messageId)
            ?? nodes.find(item => item.dataset.turnId === turnId);
          node?.scrollIntoView({ behavior: 'auto', block: 'start' });
        }));
  };
  const latestTodoItems = useMemo(() => extractLatestTodoItems(allMessages), [allMessages]);

  useEffect(() => {
    if (todoPanelDismissed && allMessages.length > todoDismissedAtRef.current) {
      const hasNewTodoWrite = allMessages.slice(todoDismissedAtRef.current).some(
        (m) => m.type === 'tool_use' && isTodoWriteToolName(m.metadata?.toolName as string | undefined)
      );
      if (hasNewTodoWrite) setTodoPanelDismissed(false);
    }
  }, [allMessages.length, todoPanelDismissed]);

  const showTodoPanel = !todoPanelDismissed && latestTodoItems !== null && latestTodoItems.length > 0;

  // Floating VNC window rect (x=-1 means auto-position on first show).
  // Width/height default to a 16:9 ratio that matches VNC_NATIVE so the
  // floating window fills with zero letterbox bars out of the box.
  const [vncWindowRect, setVncWindowRect] = useState({ x: -1, y: -1, w: 960, h: 540 });

  // Drag deliverables split divider
  const onSplitDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    vncDragRef.current = { startX: e.clientX, startRatio: vncSplitRatio };
    const onMove = (ev: MouseEvent) => {
      if (!vncDragRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const relX = (ev.clientX - rect.left) / rect.width;
      setVncSplitRatio(Math.min(Math.max(relX, 0.2), 0.8));
    };
    const onUp = () => {
      vncDragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [vncSplitRatio]);

  // Drag floating VNC window by header
  const onVncWindowDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startRect = { ...vncWindowRect };
    if (vncIframeRef.current) vncIframeRef.current.style.pointerEvents = 'none';
    const onMove = (ev: MouseEvent) => {
      const newX = startRect.x + (ev.clientX - startX);
      const newY = startRect.y + (ev.clientY - startY);
      setVncWindowRect((prev) => ({ ...prev, x: newX, y: newY }));
    };
    const onUp = () => {
      if (vncIframeRef.current) vncIframeRef.current.style.pointerEvents = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [vncWindowRect]);

  // Resize floating VNC window from edges/corners
  const onVncWindowResizeStart = useCallback((edge: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startRect = { ...vncWindowRect };
    if (vncIframeRef.current) vncIframeRef.current.style.pointerEvents = 'none';
    const MIN_W = 320;
    const MIN_H = 200;
    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setVncWindowRect(() => {
        let { x, y, w, h } = startRect;
        if (edge.includes('e')) w = Math.max(MIN_W, w + dx);
        if (edge.includes('s')) h = Math.max(MIN_H, h + dy);
        if (edge.includes('w')) {
          const newW = Math.max(MIN_W, w - dx);
          x = x + (w - newW);
          w = newW;
        }
        if (edge.includes('n')) {
          const newH = Math.max(MIN_H, h - dy);
          y = y + (h - newH);
          h = newH;
        }
        return { x, y, w, h };
      });
    };
    const onUp = () => {
      if (vncIframeRef.current) vncIframeRef.current.style.pointerEvents = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [vncWindowRect]);

  // Poll for VNC URL when sandbox session is running
  useEffect(() => {
    setVncUrl(null);
    setVncToken(null);
    vncInstanceRef.current = null;
    setShowVncModal(false);

    // Poll for VNC URL when session is in sandbox mode.
    // Keep polling regardless of streaming state — the VNC URL becomes available
    // once the container is ready, which may be before or after streaming starts.
    const isSandbox = currentSession?.executionMode === 'sandbox';
    if (!isSandbox || !currentSession) return;
    // 快速档位不起沙箱（host 侧直接推理），executionMode 仍是 sandbox 但永远拿不到
    // vncUrl —— 不拦住就是每 2s 一次的空轮询 + 一串 404 噪音。
    if (currentSession.promptMode === 'quick') return;
    // 乐观创建阶段的临时 id（temp-<ts>）在服务端不存在：/vnc-url 现在带归属校验会回 404，
    // 每次新建会话都在控制台留一条 404 噪音（cc68b25 后 E2E 探针据此误判 FAIL）。
    // 等拿到真实 id 再开始轮询——此前沙箱本来也不可能就绪。
    if (currentSession.id.startsWith('temp-')) return;

    let cancelled = false;
    // 拿到 URL 前每 2s 轮询；拿到后转入每 30s 的无凭证探活。只有首次就绪或
    // sandbox 实例变化时才调用 vnc-url 签发 bootstrap，避免常规复核不断制造
    // ticket/cookie。沙箱被 idle 回收后清空 URL，停止 noVNC 重连。
    const poll = async () => {
      if (cancelled || !currentSession) return;
      const state = await coworkService.getSessionVncState(currentSession.id);
      if (cancelled) return;
      if (state?.ready && state.instance) {
        if (shouldReplaceVncBootstrap(vncInstanceRef.current, state.instance)) {
          const info = await coworkService.getSessionVncUrl(currentSession.id);
          if (cancelled) return;
          if (!info) {
            vncPollTimerRef.current = setTimeout(poll, 2000);
            return;
          }
          vncInstanceRef.current = info.instance;
          setVncUrl(info.vncUrl);
          setVncToken(info.t);
        }
        vncPollTimerRef.current = setTimeout(poll, 30000);
        return;
      }
      // 之前已就绪、现在拿不到 → 沙箱已回收，卸载预览停止重连。
      setVncUrl(null);
      setVncToken(null);
      vncInstanceRef.current = null;
      vncPollTimerRef.current = setTimeout(poll, 2000);
    };

    poll();

    return () => {
      cancelled = true;
      if (vncPollTimerRef.current) {
        clearTimeout(vncPollTimerRef.current);
        vncPollTimerRef.current = null;
      }
    };
  }, [currentSession?.id, currentSession?.executionMode, currentSession?.promptMode]);

  // Create / destroy the persistent VNC wrapper + iframe once per vncViewerUrl.
  // Both live on document.body and are never reparented afterwards — see
  // position tracking useLayoutEffect below.
  // useLayoutEffect so that the wrapper exists before the sibling position
  // effect reads it on the very render where vncViewerUrl first becomes set.
  useLayoutEffect(() => {
    if (!vncViewerUrl) {
      if (vncIframeWrapRef.current) {
        vncIframeWrapRef.current.remove();
        vncIframeWrapRef.current = null;
        vncIframeDomRef.current = null;
        vncIframeRef.current = null;
      }
      return;
    }
    if (!vncIframeWrapRef.current) {
      const wrap = document.createElement('div');
      wrap.style.position = 'fixed';
      wrap.style.left = '-99999px';
      wrap.style.top = '0';
      wrap.style.width = `${VNC_NATIVE_W}px`;
      wrap.style.height = `${VNC_NATIVE_H}px`;
      wrap.style.zIndex = '50';
      wrap.style.overflow = 'hidden';
      wrap.style.pointerEvents = 'none';

      const iframe = document.createElement('iframe');
      iframe.src = vncViewerUrl;
      iframe.className = 'border-0';
      iframe.allow = 'fullscreen';
      iframe.title = 'VNC Desktop';
      iframe.setAttribute('tabindex', '-1');
      iframe.style.position = 'absolute';
      iframe.style.left = '0';
      iframe.style.top = '0';
      iframe.style.width = `${VNC_NATIVE_W}px`;
      iframe.style.height = `${VNC_NATIVE_H}px`;
      iframe.style.transformOrigin = '0 0';
      iframe.style.border = '0';

      wrap.appendChild(iframe);
      document.body.appendChild(wrap);
      vncIframeWrapRef.current = wrap;
      vncIframeDomRef.current = iframe;
      vncIframeRef.current = iframe;
    } else if (vncIframeDomRef.current && vncIframeDomRef.current.src !== vncViewerUrl) {
      // Session changed — update src. A changed proxy URL inherently requires
      // a new VNC handshake, so a reload here is expected.
      vncIframeDomRef.current.src = vncViewerUrl;
    }
  }, [vncViewerUrl]);

  // Remove the wrapper (and iframe inside) from document.body when the component unmounts.
  useEffect(() => () => {
    if (vncIframeWrapRef.current) {
      vncIframeWrapRef.current.remove();
      vncIframeWrapRef.current = null;
      vncIframeDomRef.current = null;
      vncIframeRef.current = null;
    }
  }, []);

  // Position the persistent iframe wrapper over the active visual host
  // (thumbnail slot or floating modal's content area). Scale the iframe inside
  // to either "cover" (thumbnail) or "contain" (modal) the wrapper. Never
  // reparent the iframe — noVNC session stays alive across all state changes.
  useLayoutEffect(() => {
    const wrap = vncIframeWrapRef.current;
    const iframe = vncIframeDomRef.current;
    if (!wrap || !iframe) return;

    const parkOffscreen = () => {
      wrap.style.left = '-99999px';
      wrap.style.top = '0';
      wrap.style.width = `${VNC_NATIVE_W}px`;
      wrap.style.height = `${VNC_NATIVE_H}px`;
      wrap.style.visibility = 'visible';
      wrap.style.pointerEvents = 'none';
      iframe.style.transform = 'scale(1)';
    };

    const applyRect = (el: HTMLElement, mode: 'cover' | 'contain', pointerEvents: 'auto' | 'none') => {
      const r = el.getBoundingClientRect();
      wrap.style.left = `${r.left}px`;
      wrap.style.top = `${r.top}px`;
      wrap.style.width = `${r.width}px`;
      wrap.style.height = `${r.height}px`;
      wrap.style.visibility = 'visible';
      wrap.style.pointerEvents = pointerEvents;
      const sx = r.width / VNC_NATIVE_W;
      const sy = r.height / VNC_NATIVE_H;
      const s = mode === 'cover' ? Math.max(sx, sy) : Math.min(sx, sy);
      const renderedW = VNC_NATIVE_W * s;
      const renderedH = VNC_NATIVE_H * s;
      // Cover mode: center horizontally, top-anchor vertically so the usually
      // active top portion of the remote desktop (terminal windows etc.) is
      // what shows, and the empty desktop at the bottom is what gets cropped.
      // Contain mode: center both axes so any letterbox bars are balanced.
      const offX = (r.width - renderedW) / 2;
      const offY = mode === 'cover' ? 0 : (r.height - renderedH) / 2;
      iframe.style.transform = `translate(${offX}px, ${offY}px) scale(${s})`;
    };

    let activeHost: HTMLElement | null = null;
    let mode: 'cover' | 'contain' = 'cover';
    let pe: 'auto' | 'none' = 'none';

    if (showVncModal && vncUrl) {
      activeHost = vncFloatingHostRef.current;
      mode = 'contain';
      pe = 'auto';
    } else if (vncUrl && !isMobile && showVncDock && (showDeliverables || showTodoPanel || showTerminal)) {
      activeHost = vncThumbnailRef.current;
      mode = 'cover';
      pe = 'none';
    }

    if (!activeHost) {
      parkOffscreen();
      return;
    }

    applyRect(activeHost, mode, pe);

    const updateRect = () => {
      if (activeHost) applyRect(activeHost, mode, pe);
    };
    const ro = new ResizeObserver(updateRect);
    ro.observe(activeHost);
    const onWin = () => updateRect();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [vncUrl, showVncModal, showDeliverables, showTodoPanel, showTerminal, showVncDock, isMobile, vncWindowRect]);

  // VNC thumbnail click → open floating window (instant, no animation)
  const handleVncThumbnailClick = useCallback(() => {
    // If the VNC iframe may have failed to load (rfb.js 502), force a reload
    // so it gets a fresh chance to connect through the now-warmed port-forward.
    if (vncIframeDomRef.current) {
      try {
        const iframeWin = vncIframeDomRef.current.contentWindow as any;
        if (iframeWin && !iframeWin.__vncModuleLoaded) {
          vncIframeDomRef.current.contentWindow?.location.reload();
        }
      } catch { /* cross-origin safety */ }
    }

    // Position window on the right side if x=-1 (first show)
    let winX = vncWindowRect.x;
    let winY = vncWindowRect.y;
    const winW = vncWindowRect.w;
    const winH = vncWindowRect.h;
    if (winX < 0 || winY < 0) {
      winX = Math.max(0, window.innerWidth - winW - 24);
      winY = Math.max(0, (window.innerHeight - winH) / 2);
      setVncWindowRect((prev) => ({ ...prev, x: winX, y: winY }));
    }

    setShowVncModal(true);
  }, [vncWindowRect]);

  // VNC close → hide floating window (instant, no animation)
  const handleVncClose = useCallback(() => {
    setShowVncModal(false);
  }, []);

  // Menu and action states
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const actionButtonRef = useRef<HTMLButtonElement>(null);
  const [turnDeleteSessionId, setTurnDeleteSessionId] = useState<string | null>(null);
  useEffect(() => { setTurnDeleteSessionId(null); }, [currentSession?.id]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isExportingImage, setIsExportingImage] = useState(false);
  const exportControllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const cancelExport = () => {
      exportControllerRef.current?.abort(); exportControllerRef.current = null;
      setIsExportingImage(false);
    };
    window.addEventListener('auth:identityChanging', cancelExport);
    return () => { window.removeEventListener('auth:identityChanging', cancelExport); cancelExport(); };
  }, [currentSession?.id]);

  // Rename states
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  const ignoreNextBlurRef = useRef(false);

  // Reset rename value when session changes
  useEffect(() => {
    if (!isRenaming && currentSession) {
      setRenameValue(currentSession.title);
      ignoreNextBlurRef.current = false;
    }
  }, [isRenaming, currentSession?.title]);

  useEffect(() => {
    setShouldAutoScroll(true);
  }, [currentSession?.id]);

  // Focus rename input when entering rename mode
  useEffect(() => {
    if (!isRenaming) return;
    requestAnimationFrame(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    });
  }, [isRenaming]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuPosition) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !actionButtonRef.current?.contains(target)) {
        closeMenu();
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };
    const handleScroll = () => closeMenu();
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [menuPosition]);

  // Menu position calculator
  const calculateMenuPosition = (height: number) => {
    const rect = actionButtonRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const menuWidth = 180;
    const padding = 8;
    const x = Math.min(
      Math.max(padding, rect.right - menuWidth),
      window.innerWidth - menuWidth - padding
    );
    const y = Math.min(rect.bottom + 8, window.innerHeight - height - padding);
    return { x, y };
  };

  const openMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isRenaming) return;
    if (menuPosition) {
      closeMenu();
      return;
    }
    const menuHeight = 300;
    const position = calculateMenuPosition(menuHeight);
    if (position) {
      setMenuPosition(position);
    }
    setShowConfirmDelete(false);
  };

  const closeMenu = () => {
    setMenuPosition(null);
    setShowConfirmDelete(false);
  };

  // Rename handlers
  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentSession) return;
    ignoreNextBlurRef.current = false;
    setIsRenaming(true);
    setShowConfirmDelete(false);
    setRenameValue(currentSession.title);
    setMenuPosition(null);
  };

  const handleRenameSave = async (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    if (!currentSession) return;
    ignoreNextBlurRef.current = true;
    const nextTitle = renameValue.trim();
    if (nextTitle && nextTitle !== currentSession.title) {
      await coworkService.renameSession(currentSession.id, nextTitle);
    }
    setIsRenaming(false);
  };

  const handleRenameCancel = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation();
    ignoreNextBlurRef.current = true;
    if (currentSession) {
      setRenameValue(currentSession.title);
    }
    setIsRenaming(false);
  };

  const handleRenameBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (ignoreNextBlurRef.current) {
      ignoreNextBlurRef.current = false;
      return;
    }
    handleRenameSave(event);
  };

  // Pin/unpin handler
  const handleTogglePin = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentSession) return;
    await coworkService.setSessionPinned(currentSession.id, !currentSession.pinned);
    closeMenu();
  };

  // Delete handlers
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowConfirmDelete(true);
    setMenuPosition(null);
  };

  /** 快照与保存分层；同步锁防重复下载，身份/会话变化后不保存旧内容。 */
  const handleShareClick = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!currentSession || exportControllerRef.current || !scrollContainerRef.current) return;
    const controller = new AbortController();
    exportControllerRef.current = controller;
    const title = currentSession.title;
    closeMenu(); setIsExportingImage(true);
    try {
      const pngBase64 = await createConversationImage(scrollContainerRef.current, controller.signal);
      controller.signal.throwIfAborted();
      const result = await coworkService.saveSessionResultImage({ pngBase64,
        defaultFileName: sanitizeExportFileName(`${title}-${formatExportTimestamp(new Date())}.png`) });
      if (!result.success) throw new Error('Image download failed');
      if (!result.canceled && !controller.signal.aborted) {
        window.dispatchEvent(new CustomEvent('app:showToast', { detail: i18nService.t('coworkExportImageSuccess') }));
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        const key = error instanceof ConversationExportError
          ? error.kind === 'size' ? 'coworkExportImageTooLarge' : 'coworkExportImageResourceFailed'
          : 'coworkExportImageFailed';
        window.dispatchEvent(new CustomEvent('app:showToast', { detail: i18nService.t(key) }));
      }
    } finally {
      if (exportControllerRef.current === controller) {
        exportControllerRef.current = null; setIsExportingImage(false);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!currentSession) return;
    await coworkService.deleteSession(currentSession.id);
    setShowConfirmDelete(false);
    if (onNavigateHome) {
      onNavigateHome();
    }
  };

  const handleCancelDelete = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setShowConfirmDelete(false);
  };

  const resolveLocalFilePath = useCallback((href: string, text: string) => {
    const hrefValue = typeof href === 'string' ? href.trim() : '';
    const textValue = typeof text === 'string' ? text.trim() : '';
    if (!hrefValue && !textValue) return null;

    // In sandbox mode, translate VM guest paths to host paths.
    const mapSandboxPath = (filePath: string): string => {
      if (
        currentSession?.executionMode !== 'sandbox' ||
        !currentSession?.cwd
      ) {
        return filePath;
      }
      const mapped = mapSandboxGuestPathToCwd(filePath, currentSession.cwd);
      return mapped ?? filePath;
    };

    const hrefRootRelative = hrefValue ? parseRootRelativePath(hrefValue) : null;
    if (hrefRootRelative) {
      return mapSandboxPath(hrefRootRelative);
    }

    const hrefPath = hrefValue ? normalizeLocalPath(hrefValue) : null;
    if (hrefPath) {
      if (hrefPath.isRelative && currentSession?.cwd) {
        return mapSandboxPath(toAbsolutePathFromCwd(hrefPath.path, currentSession.cwd));
      }
      if (hrefPath.isAbsolute) {
        return mapSandboxPath(hrefPath.path);
      }
    }

    const textRootRelative = textValue ? parseRootRelativePath(textValue) : null;
    if (textRootRelative) {
      return mapSandboxPath(textRootRelative);
    }

    const textPath = textValue ? normalizeLocalPath(textValue) : null;
    if (textPath) {
      if (textPath.isRelative && currentSession?.cwd) {
        return mapSandboxPath(toAbsolutePathFromCwd(textPath.path, currentSession.cwd));
      }
      if (textPath.isAbsolute) {
        return mapSandboxPath(textPath.path);
      }
    }

    return null;
  }, [currentSession?.cwd, currentSession?.executionMode]);

  const mapDisplayText = useCallback((value: string): string => {
    if (currentSession?.executionMode !== 'sandbox') {
      return value;
    }
    return mapSandboxGuestPathsInText(value, currentSession?.cwd);
  }, [currentSession?.cwd, currentSession?.executionMode]);

  const imageTargetClientId = currentSession ? getTargetClient(currentSession.id) : null;
  // 使用稳定的会话读取器，流式文字更新不会重复下载；会话/客户端变化则重新隔离加载。
  const loadMarkdownImage = useMemo(() => currentSession ? createSessionImageLoader({
    sessionId: currentSession.id,
    sandbox: currentSession.executionMode === 'sandbox',
    cwd: currentSession.cwd,
    getHeaders: getAuthHeaders,
    readDesktopImage: imageTargetClientId || currentSession.origin === 'desktop'
      ? async path => {
        if (!imageTargetClientId) throw new Error('桌面客户端未连接，请连接后重试');
        return relayService.readImage(imageTargetClientId, path, 1600, 90);
      }
      : undefined,
  }) : undefined, [currentSession?.id, currentSession?.origin, currentSession?.executionMode, currentSession?.cwd, imageTargetClientId]);

  // Handle inline permission response
  const handleInlinePermissionRespond = useCallback(async (result: CoworkPermissionResult) => {
    if (!pendingPermission) return;

    // Desktop session: route permission response through Relay
    const targetClientId = currentSession ? getTargetClient(currentSession.id) : null;
    if (currentSession?.origin === 'desktop') {
      if (!targetClientId) throw new Error('桌面客户端已离线，无法提交回答');
      try {
        await relayService.ensureConnected();
        await relayService.respondToPermission(targetClientId, pendingPermission.requestId, result);
        dispatch(dequeuePendingPermission({ requestId: pendingPermission.requestId }));
      } catch (err) {
        console.error('[CoworkSessionDetail] Failed to respond to Desktop permission:', err);
        throw err;
      }
      return;
    }

    // Cloud session: existing REST API path
    const cowork = window.electron?.cowork;
    if (!cowork) throw new Error(i18nService.t('coworkDraftSendFailed'));
    const response = await cowork.respondToPermission({
      requestId: pendingPermission.requestId,
      result,
    });
    if (response.success || response.expired) {
      dispatch(dequeuePendingPermission({ requestId: pendingPermission.requestId }));
      if (response.expired) window.dispatchEvent(new CustomEvent('app:showToast', { detail: response.error }));
    } else {
      throw new Error(response.error || i18nService.t('coworkDraftSendFailed'));
    }
  }, [pendingPermission, dispatch, currentSession]);

  const messages = currentSession?.messages;
  const displayItems = useMemo(
    () => messages ? buildDisplayItems(messages) : [],
    [messages]
  );
  const turns = useMemo(
    () => buildConversationTurns(displayItems),
    [displayItems]
  );
  // 与轮次底栏复用同一主用户轮次，忽略子代理和思考消息，避免费用错配。
  const latestUserTurn = useMemo(() => {
    for (let index = turns.length - 1; index >= 0; index--) {
      if (turns[index].userMessage) return turns[index].userMessage;
    }
    return null;
  }, [turns]);

  if (!currentSession) {
    return null;
  }

  const renderConversationTurns = () => {
    if (turns.length === 0) {
      if (!isStreaming) return null;
      return (
        <div data-export-role="assistant-block">
          <AssistantTurnBlock
            turn={{
              id: 'streaming-only',
              userMessage: null,
              assistantItems: [],
            }}
            sessionId={currentSession.id}
            desktop={currentSession.origin === 'desktop'}
            loadImage={loadMarkdownImage}
            resolveLocalFilePath={resolveLocalFilePath}
            showTypingIndicator
            showCopyButtons={!isStreaming}
            isTurnRunning
            onFileClick={handleFileClickFromMarkdown}
          />
        </div>
      );
    }

    return turns.map((turn, index) => {
      const isLastTurn = index === turns.length - 1;
      const showTypingIndicator = isStreaming && isLastTurn && !hasRenderableAssistantContent(turn);
      const showAssistantBlock = turn.assistantItems.length > 0 || showTypingIndicator || !!readTurnSummary(turn.userMessage);

      return (
        <React.Fragment key={turn.id}>
          {turn.userMessage && (
            <div data-export-role="user-message" data-turn-id={turn.id} tabIndex={-1} className={searchTarget.turnId === turn.id ? 'ring-1 ring-inset ring-claude-accent/40 rounded-xl' : undefined}>
              <UserMessageItem message={turn.userMessage} skills={skills} executionMode={currentSession?.executionMode} sessionId={currentSession?.id} />
            </div>
          )}
          {/* showCopyButtons 只对"当前正在输出的那一轮"关闭：此前用会话级 !isStreaming，
              一发新指令就把所有历史已完成轮次的复制按钮与完成时间 pill 一起藏掉了。 */}
          {showAssistantBlock && (
            <div data-export-role="assistant-block">
              <AssistantTurnBlock
                turn={turn}
                credit={sessionCredit}
                searchFocus={searchTarget.turnId === turn.id ? searchTarget.serial : 0}
                searchMessageId={searchTarget.turnId === turn.id ? searchTarget.messageId : undefined}
                showThinking={showThinking}
                sessionId={currentSession.id}
                desktop={currentSession.origin === 'desktop'}
                loadImage={loadMarkdownImage}
                resolveLocalFilePath={resolveLocalFilePath}
                mapDisplayText={mapDisplayText}
                showTypingIndicator={showTypingIndicator}
                showCopyButtons={!(isStreaming && isLastTurn)}
                isTurnRunning={isStreaming && isLastTurn}
                onFileClick={handleFileClickFromMarkdown}
                recoveryEnabled={isLastTurn && !isStreaming && currentSession.status !== 'running' && queuedTurnCount === 0 && !pendingPermission}
                onContinue={onContinue}
              />
            </div>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div
      ref={detailRootRef}
      className="flex-1 flex flex-col dark:bg-claude-darkBg bg-claude-bg h-full relative"
    >
      {/* 全局拖拽文件遮罩 */}
      {isDraggingOver && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center bg-claude-accent/5 dark:bg-claude-accent/10 border-2 border-dashed border-claude-accent/40 rounded-lg">
          <div className="flex flex-col items-center gap-2 text-claude-accent">
            <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <span className="text-sm font-medium">{i18nService.t('coworkDropFileHint')}</span>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="draggable flex h-12 items-center justify-between gap-3 overflow-hidden px-4 border-b dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface/50 bg-claude-surface/50 shrink-0">
        {/* Left side: Toggle buttons (when collapsed) + Title + execution / IM badges */}
        <div className="flex h-full min-w-0 flex-1 items-center gap-2 overflow-hidden">
          {isSidebarCollapsed && (
            <div className={`non-draggable flex items-center gap-1 ${isMac && !isMobile ? 'pl-[68px]' : ''}`}>
              <button
                type="button"
                onClick={onToggleSidebar}
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
              >
                <SidebarToggleIcon className="h-4 w-4" isCollapsed={true} />
              </button>
              <button
                type="button"
                onClick={onNewChat}
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
              >
                <ComposeIcon className="h-4 w-4" />
              </button>
            </div>
          )}
          {isRenaming ? (
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleRenameSave(e);
                }
                if (e.key === 'Escape') {
                  handleRenameCancel(e);
                }
              }}
              onBlur={handleRenameBlur}
              className="non-draggable min-w-0 max-w-[300px] rounded-lg border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkBg bg-claude-bg px-2 py-1 text-sm font-medium dark:text-claude-darkText text-claude-text focus:outline-none focus:ring-2 focus:ring-claude-accent"
            />
          ) : (
            <h1 className="min-w-[72px] flex-1 truncate whitespace-nowrap text-sm leading-none font-medium dark:text-claude-darkText text-claude-text" title={currentSession.title || i18nService.t('coworkNewSession')}>
              {currentSession.kind === 'secretary' && currentSession.origin !== 'desktop'
                ? <SecretaryIdentity key={currentSession.id} />
                : currentSession.title || i18nService.t('coworkNewSession')}
            </h1>
          )}
          {currentSession.origin !== 'desktop' && currentSession.promptMode !== 'quick' && !currentSession.id.startsWith('temp-') && <BackgroundTasksPanel key={`tasks-${currentSession.id}`} sessionId={currentSession.id} locate={task => {
            const anchor = backgroundTaskAnchor(task, allMessages);
            return anchor ? () => locateConversationMessage(anchor.turnId, anchor.messageId) : null;
          }} />}
          {currentSession.origin !== 'desktop' && currentSession.agentSnapshot && <AgentSnapshotBadge key={currentSession.id} snapshot={currentSession.agentSnapshot} />}
          {!currentSession.id.startsWith('temp-') && <button type="button" title={i18nService.t('coworkCopyLink')} aria-label={i18nService.t('coworkCopyLink')}
            className="rounded-lg p-1.5 hover:bg-black/5 dark:hover:bg-white/10" onClick={() => {
              const clientId = currentSession.origin === 'desktop' ? getTargetClient(currentSession.id) : null;
              if (currentSession.origin === 'desktop' && !clientId) return;
              const link = buildConversationLink(window.location.href, { sessionId: currentSession.id, ...(clientId ? { clientId } : {}) });
              void navigator.clipboard.writeText(link).then(() => window.dispatchEvent(new CustomEvent('app:showToast', { detail: i18nService.t('coworkLinkCopied') })))
                .catch(() => window.dispatchEvent(new CustomEvent('app:showToast', { detail: i18nService.t('coworkLinkCopyFailed') })));
            }}><LinkIcon className="h-4 w-4" /></button>}
          {/* 快速档位不起沙箱，就不该挂"沙箱"徽标——此前徽标照亮而工具全无，
              用户以为能跑代码，模型只能反复解释自己没有执行面。 */}
          {currentSession.promptMode !== 'quick' && (
            <ExecutionBadge mode={currentSession.executionMode} requestedMode={currentSession.requestedExecutionMode} />
          )}
          {currentSession.kind === 'secretary' && (
            <PromptModeToggle
              sessionId={currentSession.id}
              mode={currentSession.promptMode === 'quick' ? 'quick' : 'collaboration'}
              disabled={currentSession.status === 'running'}
              onSwitched={(mode) => {
                dispatch(updateSessionPromptMode({ sessionId: currentSession.id, promptMode: mode }));
              }}
              onError={(message) => {
                window.dispatchEvent(new CustomEvent('app:showToast', { detail: { message } }));
              }}
            />
          )}
          {currentSession.imMetadata && (
            <span
              className={`non-draggable inline-flex min-w-0 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-1.5 py-0.5 text-xs ${
                currentSession.imMetadata.instanceDeletedAt
                  ? 'border-amber-500/20 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                  : 'border-blue-500/20 bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
              }`}
              title={currentSession.imMetadata.instanceName}
            >
              <IMPlatformBadgeIcon platform={currentSession.imMetadata.platform} />
              <span className="max-w-[160px] truncate">{currentSession.imMetadata.instanceName}</span>
            </span>
          )}
          {/* 桌面端/设备名徽标已移除:设备归属由侧栏环境选择器承担 */}
          {currentSession.origin && currentSession.origin !== 'web' && currentSession.origin !== 'desktop' && (
            <span className="inline-flex items-center rounded-full bg-blue-500/10 text-blue-500 dark:text-blue-400 border border-blue-500/30 px-2 py-0.5 text-[10px] font-semibold tracking-wide">
              {i18nService.t('coworkSourceApp')}
            </span>
          )}
        </div>

        {/* Right side: context usage + supported Web actions */}
        <div className="non-draggable flex min-w-0 max-w-[75%] items-center gap-2 whitespace-nowrap">
          <SessionCreditOverview snapshot={sessionCredit} latestTurnIds={latestUserTurn?.metadata?.billingTurnIds} running={currentSession.status === 'running'} />
          <CoworkTokenBar session={currentSession} />
          <div className="flex items-center gap-1">
          {/* TodoWrite panel toggle button */}
          {latestTodoItems && latestTodoItems.length > 0 && (
            <button
              type="button"
              onClick={() => {
                if (showTodoPanel) {
                  todoDismissedAtRef.current = allMessages.length;
                  setTodoPanelDismissed(true);
                } else {
                  setTodoPanelDismissed(false);
                  setShowDeliverables(false);
                  setShowTerminal(false);
                }
              }}
              className={`p-1.5 rounded-lg transition-colors ${
                showTodoPanel
                  ? 'dark:text-indigo-400 text-indigo-600 dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover'
                  : 'dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
              }`}
              aria-label={i18nService.t('coworkTodoPanelTitle')}
              title={i18nService.t('coworkTodoPanelTitle')}
            >
              <ClipboardDocumentListIcon className="h-4 w-4" />
            </button>
          )}

          {/* Sandbox desktop (VNC) window button — 顶栏直达入口，不再依赖交付物面板里的缩略图 */}
          {vncUrl && (
            <button
              type="button"
              onClick={() => {
                if (showVncModal) {
                  // 浮窗 → 收回面板停靠
                  setShowVncModal(false);
                  setShowVncDock(true);
                  if (!showDeliverables && !showTodoPanel && !showTerminal) setShowTerminal(true);
                  return;
                }
                const panelOpen = showDeliverables || showTodoPanel || showTerminal;
                if (panelOpen && showVncDock) {
                  setShowVncDock(false);
                } else {
                  setShowVncDock(true);
                  if (!panelOpen) {
                    setShowTerminal(true);
                    setShowDeliverables(false);
                    todoDismissedAtRef.current = allMessages.length;
                    setTodoPanelDismissed(true);
                  }
                }
              }}
              className={`p-1.5 rounded-lg transition-colors ${
                showVncModal || (showVncDock && (showDeliverables || showTodoPanel || showTerminal))
                  ? 'dark:text-emerald-400 text-emerald-600 dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover'
                  : 'dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
              }`}
              aria-label={i18nService.t('sandboxPreview')}
              title={i18nService.t('sandboxPreview')}
            >
              <ComputerDesktopIcon className="h-4 w-4" />
            </button>
          )}

          {/* Terminal toggle button (only when a sandbox is live) */}
          {vncUrl && (
            <button
              type="button"
              onClick={() => {
                setShowTerminal((v) => {
                  const next = !v;
                  if (next) {
                    setShowDeliverables(false);
                    todoDismissedAtRef.current = allMessages.length;
                    setTodoPanelDismissed(true);
                  }
                  return next;
                });
              }}
              className={`p-1.5 rounded-lg transition-colors ${
                showTerminal
                  ? 'dark:text-emerald-400 text-emerald-600 dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover'
                  : 'dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
              }`}
              aria-label={i18nService.t('coworkTerminalTitle')}
              title={i18nService.t('coworkTerminalTitle')}
            >
              <CommandLineIcon className="h-4 w-4" />
            </button>
          )}

          {/* Deliverables toggle button (hidden for desktop-origin sessions) */}
          {currentSession?.origin !== 'desktop' && (
            <button
              type="button"
              onClick={() => {
                setShowDeliverables((v) => !v);
                if (!showDeliverables) { todoDismissedAtRef.current = allMessages.length; setTodoPanelDismissed(true); setShowTerminal(false); }
              }}
              className={`p-1.5 rounded-lg transition-colors ${
                showDeliverables
                  ? 'dark:text-emerald-400 text-emerald-600 dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover'
                  : 'dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover'
              }`}
              aria-label={i18nService.t('coworkDeliverablesTitle')}
              title={i18nService.t('coworkDeliverablesTitle')}
            >
              <FolderIcon className="h-4 w-4" />
            </button>
          )}

          {/* Menu button */}
          <button
            ref={actionButtonRef}
            type="button"
            onClick={openMenu}
            className="p-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
            aria-label={i18nService.t('coworkSessionActions')}
          >
            <EllipsisHorizontalIcon className="h-5 w-5" />
          </button>
          <WindowTitleBar inline className="ml-1" />
          </div>
        </div>
      </div>

      {searchOpen && <ConversationSearch key={currentSession.id} messages={allMessages} onClose={() => { setSearchOpen(false); promptInputRef.current?.focus(); }} onLocate={(turnId, messageId) => {
        locateConversationMessage(turnId, messageId);
      }} />}

      {memoryFilesOpen && <MemoryFilesDialog key={currentSession.id} onClose={() => setMemoryFilesOpen(false)} />}

      {/* Floating Menu */}
      {menuPosition && (
        <div
          ref={menuRef}
          className="fixed z-50 min-w-[180px] rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface shadow-popover popover-enter overflow-hidden"
          style={{ top: menuPosition.y, left: menuPosition.x }}
          role="menu"
        >
          <button type="button" onClick={() => { setSearchOpen(true); setMenuPosition(null); }} className="w-full px-3 py-2 text-left text-sm dark:text-claude-darkText text-claude-text">
            {i18nService.t('conversationSearch')}
          </button>
          {window.electron.cowork.listMemoryFiles && <button type="button" role="menuitem" onClick={() => { setMemoryFilesOpen(true); closeMenu(); }} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-claude-text dark:text-claude-darkText hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover">
            <DocumentTextIcon className="h-4 w-4" />{i18nService.t('memoryCatalogPersonal')}
          </button>}
          <button type="button" role="menuitemcheckbox" aria-checked={showThinking} onClick={toggleThinking}
            className="w-full px-3 py-2 text-left text-sm dark:text-claude-darkText text-claude-text hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover">
            {showThinking ? '✓ ' : ''}{i18nService.t('showThinkingProcess')}
          </button>
          <button
            type="button"
            onClick={handleRenameClick}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm dark:text-claude-darkText text-claude-text hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
          >
            <PencilSquareIcon className="h-4 w-4" />
            {i18nService.t('renameConversation')}
          </button>
          <button
            type="button"
            onClick={handleTogglePin}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm dark:text-claude-darkText text-claude-text hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
          >
            <PushPinIcon
              slashed={currentSession.pinned}
              className={`h-4 w-4 ${currentSession.pinned ? 'opacity-60' : ''}`}
            />
            {currentSession.pinned ? i18nService.t('coworkUnpinSession') : i18nService.t('coworkPinSession')}
          </button>
          <button
            type="button"
            onClick={handleShareClick}
            disabled={isExportingImage}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm dark:text-claude-darkText text-claude-text hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ShareIcon className="h-4 w-4" />
            {i18nService.t('coworkShareSession')}
          </button>
          {currentSession.origin !== 'desktop' && <button type="button"
            disabled={isStreaming || queuedTurnCount > 0 || currentSession.status === 'running' || !currentSession.messages.some(message => message.type === 'user')}
            onClick={() => { setTurnDeleteSessionId(currentSession.id); closeMenu(); }}
            className="w-full px-3 py-2 text-left text-sm text-red-500 hover:bg-red-500/10 disabled:opacity-40">
            {i18nService.t('turnDelete_title')}
          </button>}
          <button
            type="button"
            onClick={handleDeleteClick}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <TrashIcon className="h-4 w-4" />
            {i18nService.t('deleteSession')}
          </button>
        </div>
      )}

      {turnDeleteSessionId === currentSession.id && <ConversationTurnDeleteDialog key={currentSession.id}
        sessionId={currentSession.id} messages={currentSession.messages}
        blocked={isStreaming || queuedTurnCount > 0 || currentSession.status === 'running' || Boolean(pendingPermission)}
        onClose={() => setTurnDeleteSessionId(null)} />}

      {/* Delete Confirmation Modal */}
      {showConfirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop"
          onClick={handleCancelDelete}
        >
          <div
            className="w-[calc(100%-2rem)] max-w-sm dark:bg-claude-darkSurface bg-claude-surface rounded-2xl shadow-modal overflow-hidden modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-500" />
              </div>
              <h2 className="text-base font-semibold dark:text-claude-darkText text-claude-text">
                {i18nService.t('deleteTaskConfirmTitle')}
              </h2>
            </div>

            {/* Content */}
            <div className="px-5 pb-4">
              <p className="text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {i18nService.t('deleteTaskConfirmMessage')}
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t dark:border-claude-darkBorder border-claude-border">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm font-medium rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
              >
                {i18nService.t('cancel')}
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
              >
                {i18nService.t('deleteSession')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Split container: Messages | VNC | Right Panel (Deliverables or TodoWrite) */}
      <div ref={splitContainerRef} className="flex-1 flex flex-row min-h-0 relative">
        {/* Messages panel */}
        <div
          className="relative flex flex-col min-w-0 min-h-0"
          style={{ width: isMobile ? '100%' : ((showDeliverables || showTodoPanel) ? `${vncSplitRatio * 100}%` : '100%'),
            transition: 'width 300ms ease',
          }}
        >
          <CoworkHistoryNavigator key={currentSession.id} turns={turns} scrollContainerRef={scrollContainerRef} onLocate={locateConversationMessage} />
          {/* Messages */}
          <div
            ref={scrollContainerRef}
            onScroll={handleMessagesScroll}
            className="flex-1 overflow-y-auto min-h-0 pt-3 [overflow-anchor:none]"
          >
            <div ref={messagesContentRef}>
            {/* 场景顶部卡片 — 仅信息展示，不含 workflow，触发提示可点击回填输入框 */}
            {activeScenario && (() => {
              const triggerPrompts = parseScenarioTriggers(activeScenario.trigger_prompts);
              const greeting = activeScenario.greeting || '';
              const fillPromptIntoInput = (text: string) => {
                promptInputRef.current?.setValue(text);
                promptInputRef.current?.focus();
              };
              return (
                <div className="px-4 pb-3">
                  <div className="max-w-3xl mx-auto space-y-3">
                    <div className="relative rounded-2xl border dark:border-claude-accent/30 border-claude-accent/20 dark:bg-claude-accent/5 bg-claude-accent/[0.03] px-5 py-4 shadow-sm">
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-claude-accent/10 text-claude-accent overflow-hidden text-xl">
                          <ScenarioAppIcon scenario={activeScenario} size={40} className="rounded-xl" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold dark:text-claude-darkText text-claude-text">{activeScenario.name}</span>
                            {activeScenario.group_name && (
                              <span className="text-xs px-1.5 py-0.5 rounded-full bg-claude-accent/10 text-claude-accent font-medium">{activeScenario.group_name}</span>
                            )}
                          </div>
                          {activeScenario.description && (
                            <p className="mt-1 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary line-clamp-2">
                              {activeScenario.description}
                            </p>
                          )}
                          {activeScenario.skills && activeScenario.skills.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              <span className="text-[10px] dark:text-claude-darkTextSecondary text-claude-textSecondary mr-0.5">{i18nService.t('scenariosLinkedSkills')}</span>
                              {activeScenario.skills.map((skill) => (
                                <span key={skill.skill_id} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] dark:bg-white/5 bg-black/[0.03] dark:text-claude-darkTextSecondary text-claude-textSecondary">
                                  {skill.display_name || skill.skill_name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {greeting && (
                      <div className="rounded-2xl dark:bg-claude-darkSurface bg-white border dark:border-claude-darkBorder border-claude-border px-4 py-3 shadow-sm">
                        <p className="text-sm dark:text-claude-darkText text-claude-text whitespace-pre-wrap leading-relaxed">
                          {greeting}
                        </p>
                      </div>
                    )}
                    {triggerPrompts.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {triggerPrompts.map((p, i) => {
                          const label = p.label || p.prompt || '';
                          const fullPrompt = p.prompt || p.label || '';
                          if (!label) return null;
                          return (
                            <button
                              key={i}
                              type="button"
                              title={fullPrompt}
                              onClick={() => fillPromptIntoInput(fullPrompt)}
                              className="group flex items-center gap-2 text-left rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-white hover:border-claude-accent/50 hover:dark:border-claude-accent/40 px-3 py-2.5 text-[13px] dark:text-claude-darkText text-claude-text transition-colors shadow-sm"
                            >
                              <span className="flex-1 truncate">{label}</span>
                              <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 dark:text-claude-darkTextSecondary text-claude-textSecondary group-hover:text-claude-accent transition-colors" />
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            {renderConversationTurns()}


            {/* Quick Actions - inline after messages, scrolls with conversation, per-session */}
            {!pendingPermission && currentSession.messages.length > 0 && (
              <CoworkQuickActions
                key={currentSession.id}
                sessionId={currentSession.id}
                messages={currentSession.messages}
                isStreaming={isStreaming}
                onAction={(prompt) => onContinue(prompt)}
              />
            )}

            <div ref={messagesEndRef} className="h-20" />
            </div>
          </div>
          {!shouldAutoScroll && <button type="button" onClick={() => setShouldAutoScroll(true)}
            className="absolute bottom-40 right-4 z-10 rounded-full border border-claude-border dark:border-claude-darkBorder bg-claude-surface dark:bg-claude-darkSurface px-3 py-1.5 text-xs shadow-sm">
            ↓ {i18nService.t('conversationLatest')}
          </button>}

          {/* VNC thumbnail moved to right panel bottom (EE layout) */}

          {/* Streaming Activity Bar */}


          {/* Active Scenario Badge */}
          {activeScenario && (
            <div className="shrink-0 px-4 pt-1">
              <div className="max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-claude-accent/10 dark:bg-claude-accent/15 border border-claude-accent/20">
                  <ScenarioAppIcon scenario={activeScenario} size={16} className="rounded" />
                  <span className="text-xs font-medium text-claude-accent">{activeScenario.name}</span>
                  <button
                    onClick={onDismissScenario}
                    className="ml-0.5 p-0.5 rounded-full hover:bg-claude-accent/20 transition-colors"
                    title="取消场景"
                  >
                    <XMarkIcon className="h-3 w-3 text-claude-accent" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="shrink-0 px-6 pb-5 pt-3 md:px-10">
            <div className="mx-auto max-w-5xl">
              <CoworkQueuedMessagesBar sessionId={currentSession?.id ?? null} />
              {/* 快速档位不起沙箱：运行环境选择器在这一档没有意义（选了也不会被用到），
                  showSandboxSelector 因此按档位关掉。 */}
          {pendingPermission && (
            <FollowupDrawer key={currentSession.id + ':' + pendingPermission.requestId} language={i18nService.getLanguage()}>
              <CoworkInlinePermission key={pendingPermission.requestId} permission={pendingPermission} onRespond={handleInlinePermissionRespond} />
            </FollowupDrawer>
          )}
          <CoworkPromptInput
                ref={promptInputRef}
                cwd={currentSession?.cwd}
                executionMode={currentSession?.executionMode}
                sessionId={currentSession?.id}
                activeScenario={activeScenario}
                onSelectScenario={onSelectScenario}
                onSubmit={onContinue}
                onStop={onStop}
                isStreaming={isStreaming}
                placeholder={i18nService.t(currentSession?.promptMode === 'quick' ? 'coworkQuickPlaceholder' : 'coworkContinuePlaceholder')}
                disabled={false}
                onManageSkills={onManageSkills}
                size="large"
                showModelSelector={true}
                showSandboxSelector={currentSession?.promptMode !== 'quick' && (currentSession?.origin === 'desktop' || currentSession?.executionMode === 'sandbox' || (currentSession?.origin !== 'web' && !currentSession?.sandboxProfileId))}
                sandboxDisabled={composerSandbox.locked || currentSession?.origin === 'desktop'}
                selectedDesktopClientId={currentSession?.origin === 'desktop' ? '__desktop__' : undefined}
                selectedDesktopClientName={currentSession?.origin === 'desktop'
                  ? relayService.getDesktopSessionDeviceName(currentSession.id)
                  : null}
                sandboxProfiles={sandboxProfiles}
                selectedSandboxProfileId={composerSandbox.profileId}
                onSandboxProfileChange={onSandboxProfileOverride}
                sandboxImages={sandboxImages}
                selectedSandboxImageId={composerSandbox.imageId}
                onSandboxImageChange={onSandboxImageOverride}
              />
            </div>
          </div>
        </div>

        {/* Divider: Messages | Right Panel (hidden on mobile) */}
        {!isMobile && (showDeliverables || showTodoPanel || (showTerminal && !!vncUrl)) && (
          <div
            className="shrink-0 w-1 cursor-col-resize group relative select-none"
            onMouseDown={onSplitDragStart}
          >
            <div className="absolute inset-y-0 -left-1 -right-1" />
            <div className="w-full h-full dark:bg-claude-darkBorder bg-claude-border group-hover:bg-emerald-500 group-active:bg-emerald-500 transition-colors" />
          </div>
        )}

        {/* Right Panel: Deliverables / TodoWrite / Terminal, plus VNC thumbnail at bottom */}
        {(showDeliverables || showTodoPanel || (showTerminal && !!vncUrl)) && (
          <div
            className={`flex flex-col min-w-0 min-h-0 dark:bg-claude-darkSurface bg-claude-surface ${
              isMobile ? 'fixed inset-0 z-50' : ''
            }`}
            style={isMobile ? undefined : {
              width: `${(1 - vncSplitRatio) * 100}%`,
            }}
          >
            {/* Mobile close button */}
            {isMobile && (
              <div className="flex items-center justify-between px-4 py-3 border-b dark:border-claude-darkBorder border-claude-border shrink-0">
                <span className="text-sm font-medium dark:text-claude-darkText text-claude-text">
                  {showTerminal ? i18nService.t('coworkTerminalTitle') : showTodoPanel ? i18nService.t('coworkTodoPanelTitle') : i18nService.t('coworkDeliverablesTitle')}
                </span>
                <button
                  type="button"
                  onClick={() => { setShowDeliverables(false); setShowTerminal(false); todoDismissedAtRef.current = allMessages.length; setTodoPanelDismissed(true); }}
                  className="p-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            )}
            {showDeliverables && (
              <div className="flex-1 min-h-0 overflow-hidden">
                <DeliverablePanel
                  messages={currentSession?.messages ?? []}
                  cwd={currentSession?.cwd}
                  executionMode={currentSession?.executionMode}
                  sessionId={currentSession?.id}
                  origin={currentSession?.origin}
                  highlightPath={deliverableHighlightPath}
                  registeredDeliverables={registeredDeliverables}
                />
              </div>
            )}
            {showTodoPanel && latestTodoItems && (
              <div className="flex-1 min-h-0 overflow-hidden">
                <TodoWritePanel
                  items={latestTodoItems}
                  sessionStatus={currentSession?.status}
                />
              </div>
            )}
            {showTerminal && vncUrl && (
              <div className="flex-1 min-h-0 overflow-hidden">
                <TerminalPanel
                  sessionId={currentSession.id}
                  onClose={isMobile ? () => setShowTerminal(false) : undefined}
                />
              </div>
            )}
            {/* VNC 沙箱预览停靠条 —— 与终端/交付物/Todo 同面板,固定在底部;
                点击「放大」才弹出浮窗,预览本体不再直接弹出 */}
            {vncUrl && !showVncModal && !isMobile && showVncDock && (
              <div className="shrink-0 border-t dark:border-claude-darkBorder border-claude-border">
                <div className="flex items-center gap-2 px-3 py-1.5">
                  <ComputerDesktopIcon className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary">{i18nService.t('sandboxPreview') || '沙箱预览'}</span>
                  <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-500 dark:text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 text-[9px] font-bold">
                    LIVE
                  </span>
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={handleVncThumbnailClick}
                    className="p-1 rounded-md dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
                    title={i18nService.t('coworkSandboxDesktopEnlarge')}
                    aria-label={i18nService.t('coworkSandboxDesktopEnlarge')}
                  >
                    <ArrowsPointingOutIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowVncDock(false)}
                    className="p-1 rounded-md dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
                    aria-label={i18nService.t('close')}
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div
                  ref={vncThumbnailRef}
                  onDoubleClick={handleVncThumbnailClick}
                  className="overflow-hidden relative"
                  style={{ height: 180, zIndex: 60 }}
                />
              </div>
            )}
          </div>
        )}

      </div>

      {/* Floating VNC Window */}
      {showVncModal && vncUrl && (
        <div
          ref={vncPanelRef}
          className={`fixed z-50 flex flex-col overflow-hidden shadow-2xl border border-emerald-500/30 dark:bg-claude-darkSurface bg-claude-surface ${isMobile ? 'inset-0' : 'rounded-lg'}`}
          style={isMobile ? undefined : {
            left: vncWindowRect.x,
            top: vncWindowRect.y,
            width: vncWindowRect.w,
            height: vncWindowRect.h,
          }}
        >
          {/* Draggable header */}
          <div
            onMouseDown={onVncWindowDragStart}
            className="flex items-center justify-between px-3 py-2 border-b dark:border-claude-darkBorder border-claude-border shrink-0 cursor-move select-none"
          >
            <div className="flex items-center gap-2">
              <ComputerDesktopIcon className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-medium dark:text-claude-darkText text-claude-text truncate">
                {i18nService.t('coworkSandboxDesktopTitle')}
              </span>
              <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-500 dark:text-emerald-400 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-semibold">
                LIVE
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => { if (vncViewerUrl) window.open(vncViewerUrl, '_blank'); }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
                title={i18nService.t('coworkSandboxDesktopOpenInTab')}
              >
                <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={handleVncClose}
                className="p-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div ref={vncFloatingHostRef} className="flex-1 min-h-0" />
          <div className="absolute inset-0 pointer-events-none">
            <div onMouseDown={onVncWindowResizeStart('n')} className="absolute top-0 left-2 right-2 h-1.5 cursor-n-resize pointer-events-auto" />
            <div onMouseDown={onVncWindowResizeStart('s')} className="absolute bottom-0 left-2 right-2 h-1.5 cursor-s-resize pointer-events-auto" />
            <div onMouseDown={onVncWindowResizeStart('w')} className="absolute top-2 bottom-2 left-0 w-1.5 cursor-w-resize pointer-events-auto" />
            <div onMouseDown={onVncWindowResizeStart('e')} className="absolute top-2 bottom-2 right-0 w-1.5 cursor-e-resize pointer-events-auto" />
            <div onMouseDown={onVncWindowResizeStart('nw')} className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize pointer-events-auto" />
            <div onMouseDown={onVncWindowResizeStart('ne')} className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize pointer-events-auto" />
            <div onMouseDown={onVncWindowResizeStart('sw')} className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize pointer-events-auto" />
            <div onMouseDown={onVncWindowResizeStart('se')} className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize pointer-events-auto" />
          </div>
        </div>
      )}

    </div>
  );
};

export default CoworkSessionDetail;
